import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Zap,
  ArrowLeft,
  Play,
  Download,
  MapPin,
  Calculator,
  TrendingUp,
  Shield,
  FileText,
  Layout,
  Loader2,
  CheckCircle2,
  Circle,
  Sun,
  Building2,
  Car,
  IndianRupee,
  RefreshCw,
  SlidersHorizontal,
  Landmark,
  Mail,
  Send,
  MessageSquare,
  Navigation,
  Cpu,
  Store,
  AlertTriangle,
  CheckSquare,
  Star,
  Package,
  ChevronRight,
  Pencil,
  ExternalLink,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  ComposedChart,
  Line,
  ReferenceLine,
} from "recharts";
import SiteLayoutMap from "@/components/SiteLayoutMap";
import { EditProjectSheet } from "@/components/EditProjectSheet";
import { lazy, Suspense } from "react";
const CompetitionMap = lazy(() => import("@/components/CompetitionMap"));

const CHARGER_LABELS = {
  "30kW_DC":  "30 kW DC Fast",
  "60kW_DC":  "60 kW DC Fast",
  "120kW_DC": "120 kW DC Fast",
  "150kW_DC": "150 kW Supercharger",
  "22kW_AC":  "22 kW AC Slow",
};

const ProjectView = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisStep, setAnalysisStep] = useState(0);
  const [analysisStartTime, setAnalysisStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [recalculatingROI, setRecalculatingROI] = useState(false);
  const [customROI, setCustomROI] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [shareMessage, setShareMessage] = useState("");
  const [sharing, setSharing] = useState(false);
  const [stepsRunning, setStepsRunning] = useState({});
  const [editingName, setEditingName] = useState(false);
  const [editName, setEditName] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);
  const [costToggles, setCostToggles] = useState({
    civil: true,
    electrical: true,
    solar: true,
    buildings: true,
    misc: true,
  });

  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);
  const layoutMapRef = useRef(null);
  const [activeTab, setActiveTab] = useState("cost");

  // New feature states
  const [locationLoading, setLocationLoading] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [subsidyLoading, setSubsidyLoading] = useState(false);
  const [hardwareLoading, setHardwareLoading] = useState(false);
  const [interestDialogOpen, setInterestDialogOpen] = useState(false);
  const [interestProjectId, setInterestProjectId] = useState(null);
  const [interestForm, setInterestForm] = useState({ name: "", organization: "", email: "", message: "", interest_type: "operator" });
  const [submittingInterest, setSubmittingInterest] = useState(false);

  // ROI parameter sliders
  const [roiParams, setRoiParams] = useState({
    sessions_per_charger_per_day: 8,
    average_kwh_per_session: 35,
    electricity_cost_per_kwh: 9,
    selling_price_per_kwh: 18,
    staff_monthly_cost: 50000,
    maintenance_percent: 2,

    // CAPEX multipliers
    capex_chargers_multiplier: 1.0,
    capex_civil_multiplier: 1.0,
    capex_electrical_multiplier: 1.0,
    capex_solar_multiplier: 1.0,
    capex_buildings_multiplier: 1.0,
    capex_misc_multiplier: 1.0,
  });

  // Estimated time per step (in seconds)
  const stepEstimates = {
    1: 8,   // Site classification
    2: 3,   // Layout generation (now deterministic)
    3: 1,   // SVG generation
    4: 1,   // Cost estimation (deterministic)
    5: 10,  // ROI analysis (AI)
    6: 10,  // Design rationale (AI)
    7: 10,  // Compliance (AI)
  };
  const totalEstimatedTime = Object.values(stepEstimates).reduce((a, b) => a + b, 0);

  const analysisSteps = [
    { id: 1, name: "Classifying Site", key: "site_classification" },
    { id: 2, name: "Generating Layout", key: "layout" },
    { id: 3, name: "Creating SVG", key: "svg_layout" },
    { id: 4, name: "Estimating Costs", key: "cost_estimate" },
    { id: 5, name: "Analyzing ROI", key: "roi_analysis" },
    { id: 6, name: "Design Rationale", key: "design_rationale" },
    { id: 7, name: "Compliance Check", key: "compliance_checklist" },
  ];

  // Timer effect for elapsed time
  useEffect(() => {
    let interval;
    if (analyzing && analysisStartTime) {
      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - analysisStartTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [analyzing, analysisStartTime]);

  useEffect(() => {
    fetchProject();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Initialize ROI sliders from calibrated params returned by the analysis
  useEffect(() => {
    if (!project?.roi_analysis) return;
    const calibrated = project.roi_analysis.detailed_analysis?.calibrated_params;
    if (calibrated) {
      setRoiParams(prev => ({
        ...prev,
        sessions_per_charger_per_day: calibrated.sessions_per_charger_per_day ?? prev.sessions_per_charger_per_day,
        average_kwh_per_session: calibrated.average_kwh_per_session ?? prev.average_kwh_per_session,
        electricity_cost_per_kwh: calibrated.electricity_cost_per_kwh ?? prev.electricity_cost_per_kwh,
        selling_price_per_kwh: calibrated.selling_price_per_kwh ?? prev.selling_price_per_kwh,
        staff_monthly_cost: calibrated.staff_monthly_cost ?? prev.staff_monthly_cost,
        maintenance_percent: calibrated.maintenance_percent ?? prev.maintenance_percent,
      }));
    } else if (project.roi_analysis.utilization) {
      // Fallback for older projects without calibrated_params
      setRoiParams(prev => ({
        ...prev,
        sessions_per_charger_per_day: project.roi_analysis.utilization.sessions_per_charger_per_day || prev.sessions_per_charger_per_day,
        average_kwh_per_session: project.roi_analysis.utilization.average_kwh_per_session || prev.average_kwh_per_session,
      }));
    }
  }, [project?.roi_analysis]);

  const STATUS_FLOW = ["draft", "complete", "review", "approved", "in_construction"];
  const STATUS_META = {
    draft:           { label: "Draft",           badge: "bg-[#B2E6D4] text-[#4a7068]",         next: null },
    complete:        { label: "Analyzed",        badge: "bg-emerald-500/20 text-emerald-600",   next: "Submit for Review" },
    review:          { label: "Under Review",    badge: "bg-amber-500/20 text-amber-600",       next: "Mark Approved" },
    approved:        { label: "Approved",        badge: "bg-violet-500/20 text-violet-600",     next: "Start Construction" },
    in_construction: { label: "In Construction", badge: "bg-orange-500/20 text-orange-600",    next: null },
  };

  const advanceStatus = async () => {
    const currentIdx = STATUS_FLOW.indexOf(project.status);
    if (currentIdx === -1 || currentIdx >= STATUS_FLOW.length - 1) return;
    const nextStatus = STATUS_FLOW[currentIdx + 1];
    setUpdatingStatus(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: nextStatus }),
      });
      if (res.ok) {
        setProject(prev => ({ ...prev, status: nextStatus }));
        toast.success(`Status updated to "${STATUS_META[nextStatus]?.label ?? nextStatus}"`);
      } else {
        toast.error("Failed to update status");
      }
    } catch {
      toast.error("Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const fetchProject = async () => {
    try {
      const response = await fetch(`${API}/projects/${projectId}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setProject(data);
      } else {
        toast.error("Failed to load project");
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error("Failed to load project");
    } finally {
      setLoading(false);
    }
  };

  // Debounced ROI recalculation
  const recalculateROI = useCallback(async (params) => {
    if (!project?.cost_estimate || !project?.layout) return;
    
    setRecalculatingROI(true);
    try {
      const response = await fetch(`${API}/projects/${projectId}/recalculate-roi`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(params),
      });
      
      if (response.ok) {
        const data = await response.json();
        setCustomROI(data);
      }
    } catch (error) {
      console.error("Failed to recalculate ROI:", error);
    } finally {
      setRecalculatingROI(false);
    }
  }, [project, projectId]);

  // Trigger recalculation when params change
  useEffect(() => {
    const timer = setTimeout(() => {
      if (project?.cost_estimate && project?.layout) {
        const effectiveParams = {
          ...roiParams,
          capex_civil_multiplier: costToggles.civil ? roiParams.capex_civil_multiplier : 0,
          capex_electrical_multiplier: costToggles.electrical ? roiParams.capex_electrical_multiplier : 0,
          capex_solar_multiplier: costToggles.solar ? roiParams.capex_solar_multiplier : 0,
          capex_buildings_multiplier: costToggles.buildings ? roiParams.capex_buildings_multiplier : 0,
          capex_misc_multiplier: costToggles.misc ? roiParams.capex_misc_multiplier : 0,
        };
        recalculateROI(effectiveParams);
      }
    }, 300); // Debounce 300ms

    return () => clearTimeout(timer);
  }, [roiParams, costToggles, recalculateROI, project?.cost_estimate, project?.layout]);

  const handleParamChange = (key, value) => {
    setRoiParams(prev => ({ ...prev, [key]: value }));
  };

  const runFullAnalysis = async () => {
    setAnalyzing(true);
    setAnalysisStep(0);
    setAnalysisStartTime(Date.now());
    setElapsedTime(0);

    try {
      const endpoints = [
        { name: "classify-site", step: 1 },
        { name: "generate-layout", step: 2 },
        { name: "generate-svg", step: 3 },
        { name: "estimate-cost", step: 4 },
        { name: "analyze-roi", step: 5 },
        { name: "generate-rationale", step: 6 },
        { name: "generate-compliance", step: 7 },
      ];

      for (const endpoint of endpoints) {
        setAnalysisStep(endpoint.step);
        const response = await fetch(`${API}/projects/${projectId}/${endpoint.name}`, {
          method: "POST",
          credentials: "include",
        });
        if (!response.ok) {
          let detail = `Step ${endpoint.step} failed`;
          try {
            const err = await response.json();
            detail = err.detail || detail;
          } catch (_) {}
          if (response.status === 402 || detail.toLowerCase().includes("credit") || detail.toLowerCase().includes("balance")) {
            toast.error("Anthropic API credits exhausted. Add credits at console.anthropic.com then retry.", { duration: 8000 });
          } else {
            toast.error(`Analysis failed at step ${endpoint.step}: ${detail}`);
          }
          throw new Error(detail);
        }
      }

      toast.success("Analysis complete!");
      fetchProject();
    } catch (error) {
      console.error("Analysis error:", error);
    } finally {
      setAnalyzing(false);
      setAnalysisStep(0);
    }
  };

  const downloadPDF = async () => {
    if (pdfLoading) return;
    setPdfLoading(true);
    setPdfProgress(0);

    // Animate fake progress: 0 → 85% while waiting, then 100% on completion
    let prog = 0;
    const progressInterval = setInterval(() => {
      prog = Math.min(85, prog + Math.random() * 18 + 4);
      setPdfProgress(Math.round(prog));
    }, 500);

    try {
      // Capture current canvas SVG so the PDF matches what the user sees
      const svgStr = layoutMapRef.current?.getSvgString?.() || "";

      const response = await fetch(`${API}/projects/${projectId}/pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ svg_layout: svgStr || undefined }),
      });

      if (response.ok) {
        clearInterval(progressInterval);
        setPdfProgress(100);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `EV_Hub_Studio_Report_${projectId}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success("Report downloaded!");
      } else {
        clearInterval(progressInterval);
        toast.error("Failed to download report");
      }
    } catch (error) {
      clearInterval(progressInterval);
      toast.error("Failed to download report");
    } finally {
      setTimeout(() => {
        setPdfLoading(false);
        setPdfProgress(0);
      }, 600);
    }
  };

  const shareReport = async () => {
    if (!shareEmail.trim()) { toast.error("Please enter a recipient email"); return; }
    setSharing(true);
    try {
      const svgStr = layoutMapRef.current?.getSvgString?.() || "";
      const response = await fetch(`${API}/projects/${projectId}/share-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          recipient_email: shareEmail.trim(),
          message: shareMessage.trim() || undefined,
          svg_layout: svgStr || undefined,
        }),
      });
      if (response.ok) {
        toast.success(`Report sent to ${shareEmail}!`);
        setShareDialogOpen(false);
        setShareEmail("");
        setShareMessage("");
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Failed to send report");
      }
    } catch {
      toast.error("Failed to send report");
    } finally {
      setSharing(false);
    }
  };

  const runStep = async (endpoint, label) => {
    setStepsRunning(prev => ({ ...prev, [endpoint]: true }));
    try {
      const response = await fetch(`${API}/projects/${projectId}/${endpoint}`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        toast.success(`${label} updated`);
        fetchProject();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || `Failed to update ${label}`);
      }
    } catch {
      toast.error(`Failed to update ${label}`);
    } finally {
      setStepsRunning(prev => ({ ...prev, [endpoint]: false }));
    }
  };

  const saveMeta = async ({ name, description }) => {
    setSavingMeta(true);
    try {
      const response = await fetch(`${API}/projects/${projectId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name, description }),
      });
      if (response.ok) {
        setProject(prev => ({ ...prev, name: name ?? prev.name, description: description ?? prev.description }));
        toast.success("Saved");
      } else {
        toast.error("Failed to save");
      }
    } catch {
      toast.error("Failed to save");
    } finally {
      setSavingMeta(false);
      setEditingName(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const runLocationAnalysis = async () => {
    setLocationLoading(true);
    try {
      const resp = await fetch(`${API}/projects/${projectId}/analyze-location`, { method: "POST", credentials: "include" });
      if (resp.ok) { toast.success("Location analysis complete!"); fetchProject(); }
      else { const e = await resp.json().catch(() => ({})); toast.error(e.detail || "Location analysis failed"); }
    } catch { toast.error("Location analysis failed"); }
    finally { setLocationLoading(false); }
  };

  const savePinAndAnalyze = async () => {
    if (!/^\d{6}$/.test(pinInput)) { toast.error("Enter a valid 6-digit PIN code"); return; }
    setLocationLoading(true);
    try {
      // Save pin_code to site_inputs via PUT
      const patchResp = await fetch(`${API}/projects/${projectId}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ site_inputs: { ...project.site_inputs, pin_code: pinInput } }),
      });
      if (!patchResp.ok) { toast.error("Could not save PIN code"); setLocationLoading(false); return; }
      // Now run analysis
      const resp = await fetch(`${API}/projects/${projectId}/analyze-location`, { method: "POST", credentials: "include" });
      if (resp.ok) { toast.success("Location analysis complete!"); fetchProject(); setPinInput(""); }
      else { const e = await resp.json().catch(() => ({})); toast.error(e.detail || "Location analysis failed"); }
    } catch { toast.error("Location analysis failed"); }
    finally { setLocationLoading(false); }
  };

  const runSubsidyCalc = async () => {
    setSubsidyLoading(true);
    try {
      const resp = await fetch(`${API}/projects/${projectId}/calculate-subsidies`, { method: "POST", credentials: "include" });
      if (resp.ok) { toast.success("Subsidy analysis complete!"); fetchProject(); }
      else { const e = await resp.json().catch(() => ({})); toast.error(e.detail || "Subsidy calculation failed"); }
    } catch { toast.error("Subsidy calculation failed"); }
    finally { setSubsidyLoading(false); }
  };

  const runHardwareRec = async () => {
    setHardwareLoading(true);
    try {
      const resp = await fetch(`${API}/projects/${projectId}/recommend-hardware`, { method: "POST", credentials: "include" });
      if (resp.ok) { toast.success("Hardware recommendations ready!"); fetchProject(); }
      else { const e = await resp.json().catch(() => ({})); toast.error(e.detail || "Hardware recommendation failed"); }
    } catch { toast.error("Hardware recommendation failed"); }
    finally { setHardwareLoading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f2ec]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#689689] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-[#4a7068]">Loading project...</p>
        </div>
      </div>
    );
  }

  if (!project) return null;

  const costData = project.cost_estimate
    ? [
        { name: "Chargers", value: project.cost_estimate.chargers?.recommended_cost || 0 },
        { name: "Civil", value: project.cost_estimate.civil?.recommended_cost || 0 },
        { name: "Electrical", value: project.cost_estimate.electrical?.recommended_cost || 0 },
        { name: "Solar", value: project.cost_estimate.solar?.recommended_cost || 0 },
        { name: "Buildings", value: project.cost_estimate.buildings?.recommended_cost || 0 },
        { name: "Misc", value: project.cost_estimate.misc?.recommended_cost || 0 },
      ].filter((d) => d.value > 0)
    : [];

  const COLORS = ["#689689", "#83E8BA", "#A49E8D", "#B2E6D4", "#4a7068", "#EF4444"];

  return (
    <div className="min-h-screen bg-[#f5f2ec] overflow-x-hidden" data-testid="project-view-page">
      {/* Header */}
      <header className="bg-white border-b border-[#B2E6D4] sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link
                to="/dashboard"
                className="flex items-center gap-2 text-[#4a7068] hover:text-[#689689]"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#689689] rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-[#faf9f6]" />
                </div>
                <div>
                  {editingName ? (
                    <div className="flex items-center gap-2">
                      <Input
                        autoFocus
                        className="h-7 text-sm font-bold w-48 border-[#B2E6D4] bg-white focus:border-[#83E8BA]"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveMeta({ name: editName, description: project.description });
                          if (e.key === "Escape") setEditingName(false);
                        }}
                      />
                      <Button size="sm" className="h-7 px-2 text-xs bg-[#689689] hover:bg-[#4a7068] text-[#faf9f6]"
                        disabled={savingMeta}
                        onClick={() => saveMeta({ name: editName, description: project.description })}>
                        {savingMeta ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                      </Button>
                    </div>
                  ) : (
                    <h1
                      className="font-bold text-lg text-[#504136] cursor-pointer hover:text-[#689689] transition-colors"
                      title="Click to rename"
                      onClick={() => { setEditName(project.name); setEditingName(true); }}
                    >
                      {project.name}
                    </h1>
                  )}
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-[#4a7068]">
                      {(() => {
                        const unit = project.site_inputs?.land_unit;
                        const ol = project.site_inputs?.original_length;
                        const ow = project.site_inputs?.original_width;
                        const lm = project.site_inputs?.land_length || 0;
                        const wm = project.site_inputs?.land_width || 0;
                        if (unit === "feet" && ol && ow) {
                          return `${ol}ft × ${ow}ft`;
                        }
                        return `${lm}m × ${wm}m`;
                      })()}
                    </p>
                    {project.status && STATUS_META[project.status] && (
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_META[project.status].badge}`}>
                        {STATUS_META[project.status].label}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setShareDialogOpen(true)}
                data-testid="share-report-btn"
                className="border-[#B2E6D4] text-[#4a7068] hover:bg-[#edf8f4] px-2 sm:px-4"
              >
                <Mail className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Share</span>
              </Button>
              <Button
                variant="outline"
                onClick={downloadPDF}
                disabled={pdfLoading}
                data-testid="download-pdf-btn"
                className="relative overflow-hidden border-[#B2E6D4] text-[#4a7068] hover:border-[#B2E6D4] px-2 sm:px-4"
              >
                {pdfLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" />
                    <span className="hidden sm:inline">Generating...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Export PDF</span>
                  </>
                )}
                {pdfLoading && (
                  <span
                    className="absolute inset-x-0 bottom-0 h-[2px] bg-[#2EC97E] transition-all duration-300 ease-out"
                    style={{ width: `${pdfProgress}%`, left: 0, right: "auto" }}
                  />
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditSheetOpen(true)}
                disabled={analyzing}
                className="gap-1.5 text-[#4a7068] border-[#B2E6D4] hover:bg-[#edf8f4] px-2 sm:px-4"
                data-testid="edit-site-btn"
              >
                <Pencil className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Edit Site &amp; Design</span>
              </Button>
              <Button
                onClick={runFullAnalysis}
                disabled={analyzing}
                className="bg-[#689689] hover:bg-[#4a7068] text-[#faf9f6]"
                data-testid="run-analysis-btn"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    {project.status === "complete" ? "Re-analyze" : "Run Analysis"}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Analysis Progress - Fixed position below header */}
      {analyzing && (
        <div className="bg-[#edf8f4] border-b border-[#B2E6D4] py-4 sticky top-16 z-30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 text-[#689689] animate-spin" />
                <span className="font-medium text-[#504136]">
                  Step {analysisStep}/{analysisSteps.length}: {analysisSteps[analysisStep - 1]?.name || "Starting..."}
                </span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-[#689689] font-mono">
                  {Math.floor(elapsedTime / 60)}:{(elapsedTime % 60).toString().padStart(2, '0')} elapsed
                </span>
                <span className="text-[#4a7068]">
                  Est. ~{Math.ceil(totalEstimatedTime / 60)} min total
                </span>
              </div>
            </div>
            <Progress value={(analysisStep / analysisSteps.length) * 100} className="h-2 mb-3" />
            <div className="flex justify-between">
              {analysisSteps.map((step) => (
                <div
                  key={step.id}
                  className="flex flex-col items-center"
                >
                  <div className={`text-xs ${
                    analysisStep >= step.id ? "text-[#689689] font-medium" : "text-[#4a7068]"
                  }`}>
                    {analysisStep > step.id ? (
                      <CheckCircle2 className="w-4 h-4 text-[#83E8BA]" />
                    ) : analysisStep === step.id ? (
                      <Loader2 className="w-4 h-4 animate-spin text-[#689689]" />
                    ) : (
                      <Circle className="w-4 h-4" />
                    )}
                  </div>
                  <span className="text-xs text-[#4a7068] mt-1 hidden sm:block">
                    {step.name.split(' ')[0]}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs defaultValue="cost" className="space-y-6" onValueChange={setActiveTab}>
          <div className="overflow-x-auto">
          <TabsList className="bg-[#edf8f4] border border-[#B2E6D4] w-max gap-1 p-1">
            <TabsTrigger value="cost" data-testid="tab-cost">
              <Calculator className="w-4 h-4 mr-1.5" />
              Cost
            </TabsTrigger>
            <TabsTrigger value="layout" data-testid="tab-layout">
              <Layout className="w-4 h-4 mr-1.5" />
              Layout
            </TabsTrigger>
            <TabsTrigger value="roi" data-testid="tab-roi">
              <TrendingUp className="w-4 h-4 mr-1.5" />
              ROI
            </TabsTrigger>
            <TabsTrigger value="compliance" data-testid="tab-compliance">
              <Shield className="w-4 h-4 mr-1.5" />
              Compliance
            </TabsTrigger>
            <TabsTrigger value="location" data-testid="tab-location">
              <Navigation className="w-4 h-4 mr-1.5" />
              Location Intel
            </TabsTrigger>
            <TabsTrigger value="hardware" data-testid="tab-hardware">
              <Cpu className="w-4 h-4 mr-1.5" />
              Hardware
            </TabsTrigger>
            <TabsTrigger value="schemes" data-testid="tab-schemes">
              <Landmark className="w-4 h-4 mr-1.5" />
              Schemes
            </TabsTrigger>
            <TabsTrigger value="overview" data-testid="tab-overview">
              <MapPin className="w-4 h-4 mr-1.5" />
              Overview
            </TabsTrigger>
          </TabsList>
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <motion.div key="overview" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-6">
            {project.site_classification && (
              <div className="flex justify-end">
                <Button variant="outline" size="sm" disabled={stepsRunning["classify-site"]}
                  onClick={() => runStep("classify-site", "Site classification")}>
                  {stepsRunning["classify-site"] ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                  Re-classify
                </Button>
              </div>
            )}

            {/* ── KPI Hero Row ─────────────────────────────────────────────── */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Site Type */}
              <Card className="border-0 bg-gradient-to-br from-[#689689] to-[#4a7068] text-white shadow-lg shadow-[#B2E6D4] overflow-hidden relative">
                <div className="absolute right-0 top-0 w-24 h-24 bg-white/5 rounded-full -translate-y-8 translate-x-8" />
                <CardContent className="p-5 relative">
                  <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center mb-3">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <p className="text-[#B2E6D4] text-xs font-medium uppercase tracking-widest mb-1">Site Type</p>
                  <p className="text-lg font-bold leading-tight capitalize">
                    {project.site_classification?.site_type || "Not analyzed"}
                  </p>
                  <p className="text-[#B2E6D4] text-xs mt-1 truncate">
                    {project.site_classification?.recommended_template || "Run analysis to classify"}
                  </p>
                </CardContent>
              </Card>

              {/* Chargers */}
              <Card className="border-0 bg-gradient-to-br from-[#689689] to-[#4a7068] text-white shadow-lg shadow-[#B2E6D4] overflow-hidden relative">
                <div className="absolute right-0 top-0 w-24 h-24 bg-white/5 rounded-full -translate-y-8 translate-x-8" />
                <CardContent className="p-5 relative">
                  <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center mb-3">
                    <Zap className="w-5 h-5" />
                  </div>
                  <p className="text-[#B2E6D4] text-xs font-medium uppercase tracking-widest mb-1">EV Chargers</p>
                  <p className="text-4xl font-bold leading-none">
                    {project.layout?.charger_arrangement?.count || "—"}
                  </p>
                  <p className="text-[#B2E6D4] text-xs mt-2">charging bays planned</p>
                  {project.site_inputs?.charger_config?.length > 0 && (
                    <div className="mt-2 flex flex-col gap-0.5">
                      {project.site_inputs.charger_config.map((c, i) => (
                        <p key={i} className="text-[#B2E6D4] text-xs">
                          {c.quantity}× {CHARGER_LABELS[c.type] || c.type}
                        </p>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Solar */}
              <Card className="border-0 bg-gradient-to-br from-[#A49E8D] to-[#8c8678] text-white shadow-lg shadow-[#B2E6D4] overflow-hidden relative">
                <div className="absolute right-0 top-0 w-24 h-24 bg-white/5 rounded-full -translate-y-8 translate-x-8" />
                <CardContent className="p-5 relative">
                  <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center mb-3">
                    <Sun className="w-5 h-5" />
                  </div>
                  <p className="text-[#c0b9ac] text-xs font-medium uppercase tracking-widest mb-1">Solar Capacity</p>
                  <p className="text-4xl font-bold leading-none">
                    {Math.max(0, project.layout?.solar?.estimated_kWp || 0) || "—"}
                  </p>
                  {(project.layout?.solar?.estimated_kWp ?? null) !== null && project.layout.solar.estimated_kWp <= 0
                    ? <p className="text-[#c0b9ac] text-xs mt-2">Site too small for solar canopy</p>
                    : <p className="text-[#c0b9ac] text-xs mt-2">kWp estimated</p>
                  }
                </CardContent>
              </Card>

              {/* CAPEX */}
              <Card className="border-0 bg-gradient-to-br from-[#5e4e41] to-[#504136] text-white shadow-lg shadow-[#B2E6D4] overflow-hidden relative">
                <div className="absolute right-0 top-0 w-24 h-24 bg-white/5 rounded-full -translate-y-8 translate-x-8" />
                <CardContent className="p-5 relative">
                  <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center mb-3">
                    <IndianRupee className="w-5 h-5" />
                  </div>
                  <p className="text-[#B2E6D4] text-xs font-medium uppercase tracking-widest mb-1">Total CAPEX</p>
                  <p className="text-xl font-bold leading-tight">
                    {formatCurrency(project.cost_estimate?.total_capex?.recommended)}
                  </p>
                  <p className="text-[#B2E6D4] text-xs mt-1">recommended investment</p>
                </CardContent>
              </Card>
            </div>

            {/* ── Site Profile + Intelligence ───────────────────────────────── */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Site Profile */}
              <Card className="border-[#B2E6D4] bg-white">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2 text-[#504136]">
                    <div className="w-7 h-7 bg-[#689689]/10 rounded-md flex items-center justify-center">
                      <MapPin className="w-4 h-4 text-[#689689]" />
                    </div>
                    Site Profile
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2.5">
                    {/* Dimensions — full width */}
                    <div className="col-span-2 bg-[#edf8f4] rounded-xl p-3.5 border border-[#B2E6D4]">
                      <p className="text-xs text-[#504136] font-semibold uppercase tracking-wide mb-1">Dimensions</p>
                      <p className="font-semibold text-[#504136] font-mono text-sm">
                        {(() => {
                          const unit = project.site_inputs?.land_unit;
                          const ol = project.site_inputs?.original_length;
                          const ow = project.site_inputs?.original_width;
                          const lm = project.site_inputs?.land_length || 0;
                          const wm = project.site_inputs?.land_width || 0;
                          const mToFt = (v) => v * 3.28084;
                          if (unit === "feet" && ol && ow) {
                            return `${ol}ft × ${ow}ft  (${lm.toFixed(1)}m × ${wm.toFixed(1)}m)`;
                          }
                          return `${lm.toFixed(1)}m × ${wm.toFixed(1)}m  (${mToFt(lm).toFixed(0)}ft × ${mToFt(wm).toFixed(0)}ft)`;
                        })()}
                      </p>
                    </div>
                    {/* Shape */}
                    <div className="bg-[#edf8f4] rounded-xl p-3.5 border border-[#B2E6D4]">
                      <p className="text-xs text-[#504136] font-semibold uppercase tracking-wide mb-1">Shape</p>
                      <p className="font-semibold text-[#504136] capitalize text-sm">{project.site_inputs?.land_shape || "—"}</p>
                    </div>
                    {/* Location */}
                    <div className="bg-[#edf8f4] rounded-xl p-3.5 border border-[#B2E6D4]">
                      <p className="text-xs text-[#504136] font-semibold uppercase tracking-wide mb-1">Location</p>
                      <p className="font-semibold text-[#504136] capitalize text-sm">{project.site_inputs?.location_type || "—"}</p>
                    </div>
                    {/* Access Points */}
                    <div className="bg-[#edf8f4] rounded-xl p-3.5 border border-[#B2E6D4]">
                      <p className="text-xs text-[#504136] font-semibold uppercase tracking-wide mb-1">Access Points</p>
                      <p className="font-semibold text-[#504136] text-sm">{project.site_inputs?.access_points || "—"}</p>
                    </div>
                    {/* State */}
                    <div className="bg-[#edf8f4] rounded-xl p-3.5 border border-[#B2E6D4]">
                      <p className="text-xs text-[#504136] font-semibold uppercase tracking-wide mb-1">State</p>
                      <p className="font-semibold text-[#504136] text-sm">{project.site_inputs?.state || "—"}</p>
                    </div>
                  </div>
                  {/* Amenities */}
                  <div className="mt-3">
                    <p className="text-xs text-[#504136] font-semibold uppercase tracking-wide mb-2">Planned Amenities</p>
                    {project.site_inputs?.features?.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {project.site_inputs.features.map((f) => (
                          <span key={f} className="inline-flex items-center bg-[#689689]/10 text-[#689689] text-xs font-medium px-2.5 py-1 rounded-full border border-[#689689]/20">
                            {f}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-[#4a7068] italic">No amenities selected</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Site Intelligence / Classification */}
              {project.site_classification ? (
                <Card className="border-[#B2E6D4] bg-white">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2 text-[#504136]">
                      <div className="w-7 h-7 bg-[#83E8BA]/10 rounded-md flex items-center justify-center">
                        <TrendingUp className="w-4 h-4 text-[#83E8BA]" />
                      </div>
                      Site Intelligence
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Template banner */}
                    <div className="rounded-xl bg-gradient-to-r from-[#689689] to-[#4a7068] text-white p-4 flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
                        <MapPin className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[#B2E6D4] text-xs uppercase tracking-widest mb-0.5">AI Recommended Template</p>
                        <p className="font-bold text-sm leading-tight truncate">{project.site_classification.recommended_template}</p>
                      </div>
                      <span className="shrink-0 text-xs font-semibold bg-white/20 border border-white/30 px-2.5 py-1 rounded-full capitalize whitespace-nowrap">
                        {project.site_classification.site_type}
                      </span>
                    </div>

                    {/* Opportunities */}
                    {project.site_classification.opportunities?.length > 0 && (
                      <div>
                        <p className="text-xs font-bold text-[#504136] uppercase tracking-widest mb-2 flex items-center gap-1.5">
                          <span className="text-[#83E8BA] text-base leading-none">↑</span> Opportunities
                        </p>
                        <div className="space-y-2">
                          {project.site_classification.opportunities.map((o, i) => (
                            <div key={i} className="flex items-start gap-3 bg-[#83E8BA]/10 border border-[#83E8BA]/20 rounded-xl px-3 py-2.5">
                              <div className="w-5 h-5 rounded-full bg-[#83E8BA] text-white flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold">✓</div>
                              <p className="text-sm text-[#504136] leading-snug">{o}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Constraints */}
                    {project.site_classification.constraints?.length > 0 && (
                      <div>
                        <p className="text-xs font-bold text-[#504136] uppercase tracking-widest mb-2 flex items-center gap-1.5">
                          <span className="text-[#c0b9ac] text-base leading-none">↓</span> Constraints to Address
                        </p>
                        <div className="space-y-2">
                          {project.site_classification.constraints.map((c, i) => (
                            <div key={i} className="flex items-start gap-3 bg-[#c0b9ac]/10 border border-[#c0b9ac]/20 rounded-xl px-3 py-2.5">
                              <div className="w-5 h-5 rounded-full bg-[#c0b9ac] text-white flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold">!</div>
                              <p className="text-sm text-[#A49E8D] leading-snug">{c}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-dashed border-[#B2E6D4] bg-[#edf8f4]">
                  <CardContent className="p-10 text-center flex flex-col items-center justify-center h-full">
                    <div className="w-12 h-12 bg-[#B2E6D4] rounded-full flex items-center justify-center mb-3">
                      <TrendingUp className="w-6 h-6 text-[#4a7068]" />
                    </div>
                    <p className="font-medium text-[#4a7068]">Not yet classified</p>
                    <p className="text-sm text-[#4a7068] mt-1">Run the analysis pipeline to get AI-powered site intelligence</p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* ── Design Rationale ─────────────────────────────────────────── */}
            {project.design_rationale && (
              <div className="space-y-4">
                {/* Section header */}
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-[#A49E8D]/10 rounded-md flex items-center justify-center">
                    <FileText className="w-4 h-4 text-[#A49E8D]" />
                  </div>
                  <h3 className="font-semibold text-[#504136]">Design Rationale</h3>
                  {project.design_rationale.auto_generated && (
                    <span className="text-xs bg-[#edf8f4] text-[#4a7068] px-2 py-0.5 rounded-full border border-[#B2E6D4]">auto-generated</span>
                  )}
                </div>

                {/* Row 1: Overview + Why This Layout */}
                <div className="grid lg:grid-cols-5 gap-4">
                  <Card className="border-[#B2E6D4] bg-white lg:col-span-3">
                    <CardContent className="p-5">
                      <p className="text-xs font-bold text-[#504136] uppercase tracking-widest mb-3 flex items-center gap-1.5">
                        <FileText className="w-3 h-3" /> Design Overview
                      </p>
                      <blockquote className="border-l-4 border-[#689689] bg-[#689689]/5 pl-4 pr-3 py-3 rounded-r-xl italic text-[#4a7068] text-sm leading-relaxed">
                        {project.design_rationale.design_rationale}
                      </blockquote>
                      {project.design_rationale.why_this_layout && (
                        <div className="mt-4">
                          <p className="text-xs font-bold text-[#504136] uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                            <Layout className="w-3 h-3" /> Why This Layout
                          </p>
                          <p className="text-sm text-[#4a7068] leading-relaxed">{project.design_rationale.why_this_layout}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {project.design_rationale.key_features?.length > 0 && (
                    <Card className="border-[#B2E6D4] bg-white lg:col-span-2">
                      <CardContent className="p-5">
                        <p className="text-xs font-bold text-[#504136] uppercase tracking-widest mb-3 flex items-center gap-1.5">
                          <CheckCircle2 className="w-3 h-3" /> Key Design Features
                        </p>
                        <div className="space-y-2">
                          {project.design_rationale.key_features.map((f, i) => (
                            <div key={i} className="flex items-start gap-2.5 bg-[#A49E8D]/10 border border-[#A49E8D]/20 rounded-lg px-3 py-2.5">
                              <div className="w-5 h-5 rounded-md bg-[#A49E8D] text-white flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold">{i + 1}</div>
                              <p className="text-sm text-[#504136] leading-snug">{f}</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Row 2: Tradeoffs — full-width earth callout */}
                {project.design_rationale.tradeoffs && (
                  <div className="flex items-start gap-4 bg-[#c0b9ac]/10 border border-[#c0b9ac]/20 rounded-xl px-5 py-4">
                    <div className="w-9 h-9 bg-[#c0b9ac] rounded-xl flex items-center justify-center flex-shrink-0 text-white font-bold text-base">⚖</div>
                    <div>
                      <p className="text-xs font-semibold text-[#A49E8D] uppercase tracking-widest mb-1">Design Tradeoffs</p>
                      <p className="text-sm text-[#A49E8D] leading-relaxed">{project.design_rationale.tradeoffs}</p>
                    </div>
                  </div>
                )}

                {/* Row 3: Recommendations + Risk Factors */}
                <div className="grid lg:grid-cols-2 gap-4">
                  {project.design_rationale.recommendations?.length > 0 && (
                    <Card className="border-[#B2E6D4] bg-white">
                      <CardHeader className="pb-2 pt-4 px-5">
                        <CardTitle className="text-sm flex items-center gap-2 text-[#504136] font-semibold">
                          <div className="w-5 h-5 bg-[#689689]/10 rounded flex items-center justify-center">
                            <TrendingUp className="w-3 h-3 text-[#689689]" />
                          </div>
                          Implementation Recommendations
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="px-5 pb-5">
                        <div className="space-y-2">
                          {project.design_rationale.recommendations.map((r, i) => (
                            <div key={i} className="flex items-start gap-3 bg-[#edf8f4] hover:bg-[#689689]/10 transition-colors rounded-xl px-3 py-2.5 border border-[#B2E6D4] hover:border-[#B2E6D4] group cursor-default">
                              <div className="w-6 h-6 bg-[#689689] group-hover:bg-[#83E8BA] text-white rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold transition-colors">
                                {i + 1}
                              </div>
                              <p className="text-sm text-[#4a7068] leading-snug">{r}</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {project.design_rationale.risk_factors?.length > 0 && (
                    <Card className="border-[#B2E6D4] bg-white">
                      <CardHeader className="pb-2 pt-4 px-5">
                        <CardTitle className="text-sm flex items-center gap-2 text-[#504136] font-semibold">
                          <div className="w-5 h-5 bg-red-500/10 rounded flex items-center justify-center">
                            <Shield className="w-3 h-3 text-red-500" />
                          </div>
                          Risk Factors
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="px-5 pb-5">
                        <div className="space-y-2">
                          {project.design_rationale.risk_factors.map((r, i) => (
                            <div key={i} className="flex items-start gap-3 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2.5">
                              <div className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold">!</div>
                              <p className="text-sm text-red-700 leading-snug">{r}</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            )}

            {/* ── Notes ────────────────────────────────────────────────────── */}
            <Card className="border-[#B2E6D4] bg-white">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-[#504136]">
                  <div className="w-7 h-7 bg-[#edf8f4] rounded-md flex items-center justify-center">
                    <FileText className="w-4 h-4 text-[#4a7068]" />
                  </div>
                  Notes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Add notes about this project…"
                  className="resize-none text-sm border-[#B2E6D4] bg-white focus:border-[#83E8BA]"
                  rows={3}
                  value={editNotes !== "" ? editNotes : (project.description || "")}
                  onChange={(e) => setEditNotes(e.target.value)}
                  onBlur={() => {
                    if (editNotes !== (project.description || "")) {
                      saveMeta({ name: project.name, description: editNotes });
                    }
                  }}
                />
                <p className="text-xs text-[#4a7068] mt-1">Saved automatically when you click away</p>
              </CardContent>
            </Card>
            </motion.div>
          </TabsContent>

          {/* Layout Tab */}
          <TabsContent value="layout" forceMount className="data-[state=inactive]:hidden">
            <motion.div key="layout" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-6">
            <Card className="border-0 bg-gradient-to-r from-[#689689] to-[#504136] text-white overflow-hidden relative">
              <div className="absolute right-0 top-0 w-40 h-40 bg-white/5 rounded-full -translate-y-16 translate-x-16" />
              <CardContent className="p-5 flex items-center justify-between relative">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                    <Layout className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg">{project.layout?.template_used || "Site Layout"}</p>
                    {project.layout?.notes && (
                      <p className="text-white/80 text-sm mt-0.5">{project.layout.notes}</p>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={stepsRunning["generate-layout"]}
                  onClick={() => runStep("generate-layout", "Layout")}
                  className="border-white/30 text-white hover:bg-white/10 hover:text-white bg-transparent"
                >
                  {stepsRunning["generate-layout"]
                    ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    : <RefreshCw className="w-3 h-3 mr-1" />}
                  Re-run Layout
                </Button>
              </CardContent>
            </Card>
            <SiteLayoutMap
              ref={layoutMapRef}
              layout={project.layout}
              siteInputs={project.site_inputs}
              isRerunning={!!stepsRunning["generate-layout"]}
              onRerun={() => runStep("generate-layout", "Layout")}
            />
            </motion.div>
          </TabsContent>

          {/* Cost Tab */}
          <TabsContent value="cost">
            <motion.div key="cost" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-6">
            {project.cost_estimate && (
              <div className="flex justify-end">
                <Button variant="outline" size="sm" disabled={stepsRunning["estimate-cost"]}
                  onClick={() => runStep("estimate-cost", "Cost estimate")}>
                  {stepsRunning["estimate-cost"] ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                  Re-estimate
                </Button>
              </div>
            )}
            {project.cost_estimate ? (
              <>
                {/* ── CAPEX Hero Row ──────────────────────────────────────── */}
                {(() => {
                  const ce = project.cost_estimate;
                  const adjustedCapex = ce ? {
                    min:
                      (ce.chargers?.total_cost_range?.[0] || 0) +
                      (costToggles.civil ? (ce.civil?.total_cost_range?.[0] || 0) : 0) +
                      (costToggles.electrical ? (ce.electrical?.total_cost_range?.[0] || 0) : 0) +
                      (costToggles.solar ? (ce.solar?.total_cost_range?.[0] || 0) : 0) +
                      (costToggles.buildings ? (ce.buildings?.total_cost_range?.[0] || 0) : 0) +
                      (costToggles.misc ? (ce.misc?.total_cost_range?.[0] || 0) : 0),
                    recommended:
                      (ce.chargers?.recommended_cost || 0) +
                      (costToggles.civil ? (ce.civil?.recommended_cost || 0) : 0) +
                      (costToggles.electrical ? (ce.electrical?.recommended_cost || 0) : 0) +
                      (costToggles.solar ? (ce.solar?.recommended_cost || 0) : 0) +
                      (costToggles.buildings ? (ce.buildings?.recommended_cost || 0) : 0) +
                      (costToggles.misc ? (ce.misc?.recommended_cost || 0) : 0),
                    max:
                      (ce.chargers?.total_cost_range?.[1] || 0) +
                      (costToggles.civil ? (ce.civil?.total_cost_range?.[1] || 0) : 0) +
                      (costToggles.electrical ? (ce.electrical?.total_cost_range?.[1] || 0) : 0) +
                      (costToggles.solar ? (ce.solar?.total_cost_range?.[1] || 0) : 0) +
                      (costToggles.buildings ? (ce.buildings?.total_cost_range?.[1] || 0) : 0) +
                      (costToggles.misc ? (ce.misc?.total_cost_range?.[1] || 0) : 0),
                  } : null;
                  return (
                <div className="grid md:grid-cols-3 gap-4">
                  <Card className="border-0 bg-gradient-to-br from-[#1e293b] to-[#0f172a] text-white shadow-lg overflow-hidden relative">
                    <div className="absolute right-0 top-0 w-24 h-24 bg-white/5 rounded-full -translate-y-8 translate-x-8" />
                    <CardContent className="p-5 relative">
                      <p className="text-slate-400 text-xs font-semibold uppercase tracking-widest mb-2">Minimum CAPEX</p>
                      <p className="text-2xl font-bold font-mono text-white">
                        {formatCurrency(adjustedCapex?.min)}
                      </p>
                      <p className="text-slate-400 text-xs mt-1">conservative estimate</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 bg-gradient-to-br from-[#064e3b] to-[#047857] text-white shadow-lg shadow-emerald-900/30 overflow-hidden relative">
                    <div className="absolute right-0 top-0 w-24 h-24 bg-white/10 rounded-full -translate-y-8 translate-x-8" />
                    <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/5 rounded-full translate-y-6 -translate-x-4" />
                    <CardContent className="p-5 relative">
                      <p className="text-emerald-300 text-xs font-semibold uppercase tracking-widest mb-2">Recommended CAPEX</p>
                      <p className="text-2xl font-bold font-mono text-white">
                        {formatCurrency(adjustedCapex?.recommended)}
                      </p>
                      <p className="text-emerald-300 text-xs mt-1">per charger: {formatCurrency(project.cost_estimate.cost_per_charger?.recommended)}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 bg-gradient-to-br from-[#1e293b] to-[#0f172a] text-white shadow-lg overflow-hidden relative">
                    <div className="absolute right-0 top-0 w-24 h-24 bg-white/5 rounded-full -translate-y-8 translate-x-8" />
                    <CardContent className="p-5 relative">
                      <p className="text-slate-400 text-xs font-semibold uppercase tracking-widest mb-2">Maximum CAPEX</p>
                      <p className="text-2xl font-bold font-mono text-white">
                        {formatCurrency(adjustedCapex?.max)}
                      </p>
                      <p className="text-slate-400 text-xs mt-1">premium estimate</p>
                    </CardContent>
                  </Card>
                </div>
                  );
                })()}

                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Pie Chart */}
                  <Card className="border-gray-200 bg-white">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2 text-gray-900">
                        <div className="w-7 h-7 bg-emerald-50 rounded-md flex items-center justify-center">
                          <Calculator className="w-4 h-4 text-emerald-600" />
                        </div>
                        CAPEX Distribution
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={[
                                { name: "Chargers", value: project.cost_estimate.chargers?.recommended_cost || 0 },
                                { name: "Civil", value: costToggles.civil ? (project.cost_estimate.civil?.recommended_cost || 0) : 0 },
                                { name: "Electrical", value: costToggles.electrical ? (project.cost_estimate.electrical?.recommended_cost || 0) : 0 },
                                { name: "Solar", value: costToggles.solar ? (project.cost_estimate.solar?.recommended_cost || 0) : 0 },
                                { name: "Buildings", value: costToggles.buildings ? (project.cost_estimate.buildings?.recommended_cost || 0) : 0 },
                                { name: "Misc", value: costToggles.misc ? (project.cost_estimate.misc?.recommended_cost || 0) : 0 },
                              ].filter(d => d.value > 0)}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={100}
                              paddingAngle={2}
                              dataKey="value"
                              label={({ name, percent }) =>
                                `${name} (${(percent * 100).toFixed(0)}%)`
                              }
                              labelLine={false}
                            >
                              {COLORS.map((color, index) => (
                                <Cell key={`cell-${index}`} fill={color} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Detailed Breakdown */}
                  <Card className="border-gray-200 bg-white">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2 text-gray-900">
                        <div className="w-7 h-7 bg-violet-50 rounded-md flex items-center justify-center">
                          <IndianRupee className="w-4 h-4 text-violet-600" />
                        </div>
                        Detailed Cost Breakdown
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-80">
                        <div className="space-y-4">
                          {/* Chargers */}
                          <div className="p-3 rounded-lg bg-white border border-gray-100 border-l-4 border-l-emerald-500">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="font-semibold text-gray-900">Chargers</p>
                                <p className="text-xs text-gray-500">
                                  {project.cost_estimate.chargers?.quantity}× {project.cost_estimate.chargers?.power_class}
                                </p>
                              </div>
                              <p className="font-mono font-bold text-emerald-600">
                                {formatCurrency(project.cost_estimate.chargers?.recommended_cost)}
                              </p>
                            </div>
                            <p className="text-xs text-gray-400">
                              Range: {formatCurrency(project.cost_estimate.chargers?.total_cost_range?.[0])} – {formatCurrency(project.cost_estimate.chargers?.total_cost_range?.[1])}
                            </p>
                          </div>

                          {/* Civil */}
                          <div className={`p-3 rounded-lg bg-white border border-gray-100 border-l-4 border-l-blue-500 transition-opacity ${!costToggles.civil ? 'opacity-40' : ''}`}>
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={costToggles.civil}
                                  onCheckedChange={(v) => setCostToggles(p => ({ ...p, civil: v }))}
                                />
                                <div>
                                  <p className="font-semibold text-gray-900">Civil Works</p>
                                  <p className="text-xs text-gray-500">
                                    Paving: {project.cost_estimate.civil?.paving_area_sqft} sqft,
                                    Canopy: {project.cost_estimate.civil?.canopy_area_sqft} sqft
                                  </p>
                                </div>
                              </div>
                              <p className={`font-mono font-bold text-blue-600 ${!costToggles.civil ? 'line-through' : ''}`}>
                                {formatCurrency(project.cost_estimate.civil?.recommended_cost)}
                              </p>
                            </div>
                            <p className="text-xs text-gray-400">
                              Range: {formatCurrency(project.cost_estimate.civil?.total_cost_range?.[0])} – {formatCurrency(project.cost_estimate.civil?.total_cost_range?.[1])}
                            </p>
                          </div>

                          {/* Electrical */}
                          <div className={`p-3 rounded-lg bg-white border border-gray-100 border-l-4 border-l-violet-500 transition-opacity ${!costToggles.electrical ? 'opacity-40' : ''}`}>
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={costToggles.electrical}
                                  onCheckedChange={(v) => setCostToggles(p => ({ ...p, electrical: v }))}
                                />
                                <div>
                                  <p className="font-semibold text-gray-900">Electrical</p>
                                  <p className="text-xs text-gray-500">
                                    {project.cost_estimate.electrical?.connection_type} Connection,
                                    {project.cost_estimate.electrical?.total_load_kw} kW load
                                  </p>
                                </div>
                              </div>
                              <p className={`font-mono font-bold text-violet-600 ${!costToggles.electrical ? 'line-through' : ''}`}>
                                {formatCurrency(project.cost_estimate.electrical?.recommended_cost)}
                              </p>
                            </div>
                            <p className="text-xs text-gray-400">
                              Range: {formatCurrency(project.cost_estimate.electrical?.total_cost_range?.[0])} – {formatCurrency(project.cost_estimate.electrical?.total_cost_range?.[1])}
                            </p>
                          </div>

                          {/* Solar */}
                          {project.cost_estimate.solar?.kWp > 0 && (
                            <div className={`p-3 rounded-lg bg-white border border-gray-100 border-l-4 border-l-amber-500 transition-opacity ${!costToggles.solar ? 'opacity-40' : ''}`}>
                              <div className="flex justify-between items-start mb-2">
                                <div className="flex items-center gap-2">
                                  <Switch
                                    checked={costToggles.solar}
                                    onCheckedChange={(v) => setCostToggles(p => ({ ...p, solar: v }))}
                                  />
                                  <div>
                                    <p className="font-semibold text-gray-900">Solar Plant</p>
                                    <p className="text-xs text-gray-500">
                                      {project.cost_estimate.solar?.kWp} kWp {project.cost_estimate.solar?.solar_type}
                                      {project.cost_estimate.solar?.bulk_discount_applied && " (5% bulk discount)"}
                                    </p>
                                  </div>
                                </div>
                                <p className={`font-mono font-bold text-amber-600 ${!costToggles.solar ? 'line-through' : ''}`}>
                                  {formatCurrency(project.cost_estimate.solar?.recommended_cost)}
                                </p>
                              </div>
                              <p className="text-xs text-gray-400">
                                Range: {formatCurrency(project.cost_estimate.solar?.total_cost_range?.[0])} – {formatCurrency(project.cost_estimate.solar?.total_cost_range?.[1])}
                              </p>
                            </div>
                          )}

                          {/* Buildings */}
                          {project.cost_estimate.buildings?.area_sqft > 0 && (
                            <div className={`p-3 rounded-lg bg-white border border-gray-100 border-l-4 border-l-rose-500 transition-opacity ${!costToggles.buildings ? 'opacity-40' : ''}`}>
                              <div className="flex justify-between items-start mb-2">
                                <div className="flex items-center gap-2">
                                  <Switch
                                    checked={costToggles.buildings}
                                    onCheckedChange={(v) => setCostToggles(p => ({ ...p, buildings: v }))}
                                  />
                                  <div>
                                    <p className="font-semibold text-gray-900">Buildings</p>
                                    <p className="text-xs text-gray-500">
                                      {project.cost_estimate.buildings?.area_sqft} sqft {project.cost_estimate.buildings?.building_type}
                                    </p>
                                  </div>
                                </div>
                                <p className={`font-mono font-bold text-rose-600 ${!costToggles.buildings ? 'line-through' : ''}`}>
                                  {formatCurrency(project.cost_estimate.buildings?.recommended_cost)}
                                </p>
                              </div>
                              <p className="text-xs text-gray-400">
                                Range: {formatCurrency(project.cost_estimate.buildings?.total_cost_range?.[0])} – {formatCurrency(project.cost_estimate.buildings?.total_cost_range?.[1])}
                              </p>
                            </div>
                          )}

                          {/* Miscellaneous */}
                          <div className={`p-3 rounded-lg bg-white border border-gray-100 border-l-4 border-l-orange-400 transition-opacity ${!costToggles.misc ? 'opacity-40' : ''}`}>
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={costToggles.misc}
                                  onCheckedChange={(v) => setCostToggles(p => ({ ...p, misc: v }))}
                                />
                                <div>
                                  <p className="font-semibold text-gray-900">Miscellaneous</p>
                                  <p className="text-xs text-gray-500">
                                    CCTV, Signage, 5% Contingency
                                  </p>
                                </div>
                              </div>
                              <p className={`font-mono font-bold text-orange-500 ${!costToggles.misc ? 'line-through' : ''}`}>
                                {formatCurrency(project.cost_estimate.misc?.recommended_cost)}
                              </p>
                            </div>
                            <p className="text-xs text-gray-400">
                              Range: {formatCurrency(project.cost_estimate.misc?.total_cost_range?.[0])} – {formatCurrency(project.cost_estimate.misc?.total_cost_range?.[1])}
                            </p>
                          </div>
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                </div>

                {/* Summary tile grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-white rounded-xl border border-gray-200 p-4 border-t-2 border-t-emerald-500">
                    <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1">Site Area</p>
                    <p className="font-bold text-gray-900 font-mono text-lg">{project.cost_estimate.summary?.site_area_sqm} <span className="text-sm font-normal text-gray-500">sqm</span></p>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 p-4 border-t-2 border-t-blue-500">
                    <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1">Chargers</p>
                    <p className="font-bold text-gray-900 font-mono text-lg">{project.cost_estimate.summary?.num_chargers}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 p-4 border-t-2 border-t-violet-500">
                    <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1">Total Load</p>
                    <p className="font-bold text-gray-900 font-mono text-lg">{project.cost_estimate.summary?.total_load_kw} <span className="text-sm font-normal text-gray-500">kW</span></p>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 p-4 border-t-2 border-t-amber-500">
                    <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1">Solar Capacity</p>
                    <p className="font-bold text-gray-900 font-mono text-lg">{Math.max(0, project.cost_estimate.summary?.solar_kWp || 0)} <span className="text-sm font-normal text-gray-500">kWp</span></p>
                    {(project.cost_estimate.summary?.solar_kWp ?? 0) <= 0 && (
                      <p className="text-xs text-gray-400 mt-0.5">Site too small for solar</p>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <Card className="border-[#B2E6D4] border-dashed bg-[#edf8f4]">
                <CardContent className="p-12 text-center">
                  <Calculator className="w-12 h-12 text-[#4a7068] mx-auto mb-4" />
                  <h3 className="font-semibold text-[#504136] mb-2">No Cost Estimate</h3>
                  <p className="text-[#4a7068] mb-4">Run the analysis to generate cost estimates</p>
                </CardContent>
              </Card>
            )}
            </motion.div>
          </TabsContent>

          {/* ROI Tab */}
          <TabsContent value="roi">
            <motion.div key="roi" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-6">
            {project.roi_analysis && (
              <div className="flex justify-end">
                <Button variant="outline" size="sm" disabled={stepsRunning["analyze-roi"]}
                  onClick={() => runStep("analyze-roi", "ROI analysis")}>
                  {stepsRunning["analyze-roi"] ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                  Re-run ROI
                </Button>
              </div>
            )}
            {project.roi_analysis ? (
              <>
                {/* ROI Parameter Sliders */}
                <Card className="border-[#B2E6D4] bg-[#edf8f4]">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2 text-[#504136]">
                        <SlidersHorizontal className="w-5 h-5 text-[#689689]" />
                        Adjust Utilization & Cost Parameters
                      </CardTitle>
                      {recalculatingROI && (
                        <div className="flex items-center gap-2 text-sm text-[#689689]">
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Recalculating...
                        </div>
                      )}
                    </div>
                    <CardDescription className="text-[#3d6b65] font-medium">Drag the sliders to see how different scenarios affect ROI</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-[#504136]">
                      <div className="md:col-span-2 lg:col-span-3">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-[#4a7068]">CAPEX Multipliers</p>
                          <p className="text-xs text-[#4a7068]">Use to model vendor quotes and civil/electrical variance</p>
                        </div>
                        <Separator className="my-3 bg-[#B2E6D4]" />
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <Label>Sessions per Charger/Day</Label>
                          <span className="font-mono text-sm font-medium">{roiParams.sessions_per_charger_per_day}</span>
                        </div>
                        <Slider
                          value={[roiParams.sessions_per_charger_per_day]}
                          onValueChange={([v]) => handleParamChange('sessions_per_charger_per_day', v)}
                          min={1}
                          max={20}
                          step={1}
                          className="w-full"
                          data-testid="slider-sessions"
                        />
                        <p className="text-xs text-[#4a7068]">1 = Low traffic, 20 = High traffic</p>
                      </div>

                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <Label>Avg kWh per Session</Label>
                          <span className="font-mono text-sm font-medium">{roiParams.average_kwh_per_session} kWh</span>
                        </div>
                        <Slider
                          value={[roiParams.average_kwh_per_session]}
                          onValueChange={([v]) => handleParamChange('average_kwh_per_session', v)}
                          min={10}
                          max={80}
                          step={5}
                          className="w-full"
                          data-testid="slider-kwh"
                        />
                        <p className="text-xs text-[#4a7068]">10 = Quick top-up, 80 = Full charge</p>
                      </div>

                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <Label>Electricity Cost</Label>
                          <span className="font-mono text-sm font-medium">₹{roiParams.electricity_cost_per_kwh}/kWh</span>
                        </div>
                        <Slider
                          value={[roiParams.electricity_cost_per_kwh]}
                          onValueChange={([v]) => handleParamChange('electricity_cost_per_kwh', v)}
                          min={5}
                          max={15}
                          step={0.5}
                          className="w-full"
                          data-testid="slider-elec-cost"
                        />
                      </div>

                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <Label>Selling Price</Label>
                          <span className="font-mono text-sm font-medium">₹{roiParams.selling_price_per_kwh}/kWh</span>
                        </div>
                        <Slider
                          value={[roiParams.selling_price_per_kwh]}
                          onValueChange={([v]) => handleParamChange('selling_price_per_kwh', v)}
                          min={12}
                          max={30}
                          step={1}
                          className="w-full"
                          data-testid="slider-sell-price"
                        />
                        <p className="text-xs text-[#4a7068]">Margin: ₹{(roiParams.selling_price_per_kwh - roiParams.electricity_cost_per_kwh).toFixed(1)}/kWh</p>
                      </div>

                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <Label>Staff Cost (Monthly)</Label>
                          <span className="font-mono text-sm font-medium">₹{(roiParams.staff_monthly_cost/1000).toFixed(0)}k</span>
                        </div>
                        <Slider
                          value={[roiParams.staff_monthly_cost]}
                          onValueChange={([v]) => handleParamChange('staff_monthly_cost', v)}
                          min={20000}
                          max={150000}
                          step={10000}
                          className="w-full"
                          data-testid="slider-staff"
                        />
                      </div>


                      {/* CAPEX Multipliers */}
                      <div className="space-y-3">
                        <div className="flex justify-between items-baseline">
                          <Label>Chargers CAPEX</Label>
                          <div className="text-right">
                            <span className="font-mono text-sm font-medium">{Math.round(roiParams.capex_chargers_multiplier * 100)}%</span>
                            {project.cost_estimate?.chargers?.recommended_cost > 0 && (
                              <span className="text-xs text-[#4a7068] ml-2">{formatCurrency(Math.round(project.cost_estimate.chargers.recommended_cost * roiParams.capex_chargers_multiplier))}</span>
                            )}
                          </div>
                        </div>
                        <Slider value={[roiParams.capex_chargers_multiplier]} onValueChange={([v]) => handleParamChange('capex_chargers_multiplier', v)} min={0} max={1.5} step={0.05} className="w-full" data-testid="slider-capex-chargers" />
                      </div>

                      <div className="space-y-3">
                        <div className="flex justify-between items-baseline">
                          <Label>Civil CAPEX</Label>
                          <div className="text-right">
                            {costToggles.civil ? (
                              <>
                                <span className="font-mono text-sm font-medium">{Math.round(roiParams.capex_civil_multiplier * 100)}%</span>
                                {project.cost_estimate?.civil?.recommended_cost > 0 && (
                                  <span className="text-xs text-[#4a7068] ml-2">{formatCurrency(Math.round(project.cost_estimate.civil.recommended_cost * roiParams.capex_civil_multiplier))}</span>
                                )}
                              </>
                            ) : (
                              <span className="text-xs bg-[#B2E6D4] text-[#2d4f4b] px-2 py-0.5 rounded font-semibold">Excluded</span>
                            )}
                          </div>
                        </div>
                        <Slider value={[roiParams.capex_civil_multiplier]} onValueChange={([v]) => handleParamChange('capex_civil_multiplier', v)} min={0} max={1.5} step={0.05} className="w-full" disabled={!costToggles.civil} data-testid="slider-capex-civil" />
                      </div>

                      <div className="space-y-3">
                        <div className="flex justify-between items-baseline">
                          <Label>Electrical CAPEX</Label>
                          <div className="text-right">
                            {costToggles.electrical ? (
                              <>
                                <span className="font-mono text-sm font-medium">{Math.round(roiParams.capex_electrical_multiplier * 100)}%</span>
                                {project.cost_estimate?.electrical?.recommended_cost > 0 && (
                                  <span className="text-xs text-[#4a7068] ml-2">{formatCurrency(Math.round(project.cost_estimate.electrical.recommended_cost * roiParams.capex_electrical_multiplier))}</span>
                                )}
                              </>
                            ) : (
                              <span className="text-xs bg-[#B2E6D4] text-[#2d4f4b] px-2 py-0.5 rounded font-semibold">Excluded</span>
                            )}
                          </div>
                        </div>
                        <Slider value={[roiParams.capex_electrical_multiplier]} onValueChange={([v]) => handleParamChange('capex_electrical_multiplier', v)} min={0} max={1.5} step={0.05} className="w-full" disabled={!costToggles.electrical} data-testid="slider-capex-electrical" />
                      </div>

                      <div className="space-y-3">
                        <div className="flex justify-between items-baseline">
                          <Label>Solar CAPEX</Label>
                          <div className="text-right">
                            {costToggles.solar ? (
                              <>
                                <span className="font-mono text-sm font-medium">{Math.round(roiParams.capex_solar_multiplier * 100)}%</span>
                                {project.cost_estimate?.solar?.recommended_cost > 0 && (
                                  <span className="text-xs text-[#4a7068] ml-2">{formatCurrency(Math.round(project.cost_estimate.solar.recommended_cost * roiParams.capex_solar_multiplier))}</span>
                                )}
                              </>
                            ) : (
                              <span className="text-xs bg-[#B2E6D4] text-[#2d4f4b] px-2 py-0.5 rounded font-semibold">Excluded</span>
                            )}
                          </div>
                        </div>
                        <Slider value={[roiParams.capex_solar_multiplier]} onValueChange={([v]) => handleParamChange('capex_solar_multiplier', v)} min={0} max={1.5} step={0.05} className="w-full" disabled={!costToggles.solar} data-testid="slider-capex-solar" />
                      </div>

                      <div className="space-y-3">
                        <div className="flex justify-between items-baseline">
                          <Label>Buildings CAPEX</Label>
                          <div className="text-right">
                            {costToggles.buildings ? (
                              <>
                                <span className="font-mono text-sm font-medium">{Math.round(roiParams.capex_buildings_multiplier * 100)}%</span>
                                {project.cost_estimate?.buildings?.recommended_cost > 0 && (
                                  <span className="text-xs text-[#4a7068] ml-2">{formatCurrency(Math.round(project.cost_estimate.buildings.recommended_cost * roiParams.capex_buildings_multiplier))}</span>
                                )}
                              </>
                            ) : (
                              <span className="text-xs bg-[#B2E6D4] text-[#2d4f4b] px-2 py-0.5 rounded font-semibold">Excluded</span>
                            )}
                          </div>
                        </div>
                        <Slider value={[roiParams.capex_buildings_multiplier]} onValueChange={([v]) => handleParamChange('capex_buildings_multiplier', v)} min={0} max={1.5} step={0.05} className="w-full" disabled={!costToggles.buildings} data-testid="slider-capex-buildings" />
                      </div>

                      <div className="space-y-3">
                        <div className="flex justify-between items-baseline">
                          <Label>Misc CAPEX</Label>
                          <div className="text-right">
                            {costToggles.misc ? (
                              <>
                                <span className="font-mono text-sm font-medium">{Math.round(roiParams.capex_misc_multiplier * 100)}%</span>
                                {project.cost_estimate?.misc?.recommended_cost > 0 && (
                                  <span className="text-xs text-[#4a7068] ml-2">{formatCurrency(Math.round(project.cost_estimate.misc.recommended_cost * roiParams.capex_misc_multiplier))}</span>
                                )}
                              </>
                            ) : (
                              <span className="text-xs bg-[#B2E6D4] text-[#2d4f4b] px-2 py-0.5 rounded font-semibold">Excluded</span>
                            )}
                          </div>
                        </div>
                        <Slider value={[roiParams.capex_misc_multiplier]} onValueChange={([v]) => handleParamChange('capex_misc_multiplier', v)} min={0} max={1.5} step={0.05} className="w-full" disabled={!costToggles.misc} data-testid="slider-capex-misc" />
                      </div>

                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <Label>Maintenance (%/year)</Label>
                          <span className="font-mono text-sm font-medium">{roiParams.maintenance_percent}%</span>
                        </div>
                        <Slider
                          value={[roiParams.maintenance_percent]}
                          onValueChange={([v]) => handleParamChange('maintenance_percent', v)}
                          min={1}
                          max={5}
                          step={0.5}
                          className="w-full"
                          data-testid="slider-maintenance"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* ROI Summary Cards - Use customROI if available */}
                {(() => {
                  const roi = customROI || project.roi_analysis;
                  return (
                    <>
                      {/* ── ROI KPI Hero Row ─────────────────────────── */}
                      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <Card className="border-0 bg-gradient-to-br from-[#689689] to-[#4a7068] text-white shadow-lg shadow-[#B2E6D4] overflow-hidden relative">
                          <div className="absolute right-0 top-0 w-20 h-20 bg-white/5 rounded-full -translate-y-6 translate-x-6" />
                          <CardContent className="p-5 relative">
                            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mb-3">
                              <TrendingUp className="w-4 h-4" />
                            </div>
                            <p className="text-[#B2E6D4] text-xs font-medium uppercase tracking-widest mb-1">Monthly Revenue</p>
                            <p className="text-2xl font-bold font-mono">{formatCurrency(roi.revenue?.monthly_revenue_inr)}</p>
                          </CardContent>
                        </Card>
                        <Card className="border-0 bg-gradient-to-br from-red-500 to-red-700 text-white shadow-lg shadow-red-100 overflow-hidden relative">
                          <div className="absolute right-0 top-0 w-20 h-20 bg-white/5 rounded-full -translate-y-6 translate-x-6" />
                          <CardContent className="p-5 relative">
                            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mb-3">
                              <IndianRupee className="w-4 h-4" />
                            </div>
                            <p className="text-red-100 text-xs font-medium uppercase tracking-widest mb-1">Monthly Expenses</p>
                            <p className="text-2xl font-bold font-mono">{formatCurrency(roi.expenses?.total_monthly_inr)}</p>
                          </CardContent>
                        </Card>
                        <Card className={`border-0 text-white shadow-lg overflow-hidden relative ${roi.profit?.monthly_net_inr >= 0 ? 'bg-gradient-to-br from-[#689689] to-[#83E8BA] shadow-[#B2E6D4]' : 'bg-gradient-to-br from-orange-500 to-orange-700 shadow-orange-100'}`}>
                          <div className="absolute right-0 top-0 w-20 h-20 bg-white/5 rounded-full -translate-y-6 translate-x-6" />
                          <CardContent className="p-5 relative">
                            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mb-3">
                              <Calculator className="w-4 h-4" />
                            </div>
                            <p className="text-xs font-medium uppercase tracking-widest mb-1 opacity-80">Monthly Net Profit</p>
                            <p className="text-2xl font-bold font-mono">{formatCurrency(roi.profit?.monthly_net_inr)}</p>
                          </CardContent>
                        </Card>
                        <Card className="border-0 bg-gradient-to-br from-[#A49E8D] to-[#8c8678] text-white shadow-lg shadow-[#B2E6D4] overflow-hidden relative">
                          <div className="absolute right-0 top-0 w-20 h-20 bg-white/5 rounded-full -translate-y-6 translate-x-6" />
                          <CardContent className="p-5 relative">
                            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mb-3">
                              <TrendingUp className="w-4 h-4" />
                            </div>
                            <p className="text-white/80 text-xs font-medium uppercase tracking-widest mb-1">Payback Period</p>
                            <p className="text-4xl font-bold leading-none">{roi.roi?.payback_years?.toFixed(1)}</p>
                            <p className="text-white/80 text-xs mt-1">years</p>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Revenue Breakdown Card */}
                      {(() => {
                        // Prefer new top-level fields; fall back to detailed_analysis for old docs
                        const da = roi.detailed_analysis;
                        const energyRev = roi.revenue?.energy_revenue_monthly_inr
                          || da?.revenue?.energy_revenue?.monthly_inr || 0;
                        const idleRev = roi.revenue?.idle_fee_monthly_inr
                          || da?.revenue?.idle_fee_revenue?.monthly_inr || 0;
                        const ancRev = roi.revenue?.ancillary_revenue_monthly_inr
                          || ((da?.revenue?.cafe_revenue_monthly_inr || 0) + (da?.revenue?.car_wash_revenue_monthly_inr || 0));
                        const solarSav = roi.revenue?.solar_savings_monthly_inr
                          || da?.opex?.solar_savings?.monthly_savings_inr || 0;
                        const totalRev  = roi.revenue?.monthly_revenue_inr || 0;
                        const totalExp  = roi.expenses?.total_monthly_inr || 0;
                        const netProfit = roi.profit?.monthly_net_inr || 0;
                        const flowData = [
                          { name: "Revenue",   energy: energyRev, idle: idleRev, ancillary: ancRev },
                          { name: "Expenses",  total: totalExp },
                          { name: "Net Profit", profit: Math.abs(netProfit) },
                        ];
                        return (
                          <Card className="border-[#B2E6D4] bg-white">
                            <CardHeader className="pb-3">
                              <CardTitle className="text-base flex items-center gap-2 text-[#504136]">
                                <div className="w-7 h-7 bg-[#83E8BA]/10 rounded-md flex items-center justify-center">
                                  <IndianRupee className="w-4 h-4 text-[#83E8BA]" />
                                </div>
                                Monthly Revenue Breakdown
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              {/* Revenue source pills */}
                              <div className="flex flex-wrap gap-2 mb-4">
                                {energyRev > 0 && (
                                  <div className="flex items-center gap-1.5 bg-[#83E8BA]/10 border border-[#83E8BA]/20 rounded-full px-3 py-1">
                                    <div className="w-2 h-2 rounded-full bg-[#83E8BA] flex-shrink-0" />
                                    <span className="text-xs text-[#689689] font-medium">Energy Sales</span>
                                    <span className="text-xs font-mono text-[#4a7068]">{formatCurrency(energyRev)}</span>
                                  </div>
                                )}
                                {idleRev > 0 && (
                                  <div className="flex items-center gap-1.5 bg-[#689689]/10 border border-[#689689]/20 rounded-full px-3 py-1">
                                    <div className="w-2 h-2 rounded-full bg-[#689689] flex-shrink-0" />
                                    <span className="text-xs text-[#689689] font-medium">Idle Fees</span>
                                    <span className="text-xs font-mono text-[#4a7068]">{formatCurrency(idleRev)}</span>
                                  </div>
                                )}
                                {ancRev > 0 && (
                                  <div className="flex items-center gap-1.5 bg-[#A49E8D]/10 border border-[#A49E8D]/20 rounded-full px-3 py-1">
                                    <div className="w-2 h-2 rounded-full bg-[#A49E8D] flex-shrink-0" />
                                    <span className="text-xs text-[#A49E8D] font-medium">Ancillary</span>
                                    <span className="text-xs font-mono text-[#A49E8D]">{formatCurrency(ancRev)}</span>
                                  </div>
                                )}
                                {solarSav > 0 && (
                                  <div className="flex items-center gap-1.5 bg-[#c0b9ac]/10 border border-[#c0b9ac]/20 rounded-full px-3 py-1">
                                    <div className="w-2 h-2 rounded-full bg-[#c0b9ac] flex-shrink-0" />
                                    <span className="text-xs text-[#A49E8D] font-medium">Solar Savings</span>
                                    <span className="text-xs font-mono text-[#A49E8D]">{formatCurrency(solarSav)}</span>
                                  </div>
                                )}
                              </div>
                              {/* Financial flow stacked bar */}
                              <div className="h-44">
                                <ResponsiveContainer width="100%" height="100%">
                                  <BarChart data={flowData} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                    <XAxis type="number" tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                                    <YAxis type="category" dataKey="name" width={72} tick={{ fontSize: 12 }} />
                                    <Tooltip formatter={(value, name) => [formatCurrency(value), name.charAt(0).toUpperCase() + name.slice(1)]} />
                                    <Bar dataKey="energy"    stackId="a" fill="#10b981" name="Energy Sales" />
                                    <Bar dataKey="idle"      stackId="a" fill="#3b82f6" name="Idle Fees" radius={[0, 4, 4, 0]} />
                                    <Bar dataKey="ancillary" stackId="a" fill="#8b5cf6" name="Ancillary" radius={[0, 4, 4, 0]} />
                                    <Bar dataKey="total"  fill="#ef4444" name="Total Expenses" radius={[0, 4, 4, 0]} />
                                    <Bar dataKey="profit" fill={netProfit >= 0 ? "#10b981" : "#f97316"} name="Net Profit" radius={[0, 4, 4, 0]} />
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })()}

                      <div className="grid lg:grid-cols-2 gap-6">
                        <Card className="border-[#B2E6D4] bg-white">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2 text-[#504136]">
                              <div className="w-7 h-7 bg-[#83E8BA]/10 rounded-md flex items-center justify-center">
                                <TrendingUp className="w-4 h-4 text-[#83E8BA]" />
                              </div>
                              Utilization & ROI
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-3">
                              <div className="flex justify-between items-center bg-[#edf8f4] rounded-lg px-3 py-2.5">
                                <span className="text-[#504136] text-sm font-medium">Sessions per Charger/Day</span>
                                <span className="font-mono font-medium text-[#504136]">
                                  {roi.utilization?.sessions_per_charger_per_day}
                                </span>
                              </div>
                              <div className="flex justify-between items-center bg-[#edf8f4] rounded-lg px-3 py-2.5">
                                <span className="text-[#504136] text-sm font-medium">Avg kWh per Session</span>
                                <span className="font-mono font-medium text-[#504136] text-sm">{roi.utilization?.average_kwh_per_session} kWh</span>
                              </div>
                              <div className="flex justify-between items-center bg-[#edf8f4] rounded-lg px-3 py-2.5">
                                <span className="text-[#504136] text-sm font-medium">Daily Energy Sold</span>
                                <span className="font-mono font-medium text-[#504136] text-sm">{roi.revenue?.daily_energy_kwh?.toFixed(0)} kWh</span>
                              </div>
                              <div className="flex justify-between items-center bg-[#edf8f4] rounded-lg px-3 py-2.5">
                                <span className="text-[#504136] text-sm font-medium">Utilization Rate</span>
                                <span className="font-mono font-medium text-[#504136] text-sm">{roi.utilization?.utilization_percentage}%</span>
                              </div>
                              <div className="flex justify-between items-center bg-gradient-to-r from-[#689689]/10 to-[#83E8BA]/10 border border-[#689689]/20 rounded-lg px-3 py-2.5">
                                <span className="text-[#504136] text-sm font-medium">Annual ROI</span>
                                <span className={`font-mono font-bold text-lg ${roi.roi?.annual_roi_percentage >= 0 ? 'text-[#83E8BA]' : 'text-red-600'}`}>
                                  {roi.roi?.annual_roi_percentage?.toFixed(1)}%
                                </span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

          {/* Government Schemes Tab moved below (was incorrectly nested inside ROI tab) */}

                        <Card className="border-[#B2E6D4] bg-white">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2 text-[#504136]">
                              <div className="w-7 h-7 bg-red-500/10 rounded-md flex items-center justify-center">
                                <IndianRupee className="w-4 h-4 text-red-500" />
                              </div>
                              Monthly Expenses Breakdown
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            {(() => {
                              const expenseChartData = [
                                { name: "Electricity",         value: roi.expenses?.electricity_monthly_inr || 0,              color: "#ef4444" },
                                { name: "Demand Charges",      value: roi.expenses?.demand_charge_monthly_inr || 0,            color: "#f97316" },
                                { name: "Staff",               value: roi.expenses?.staff_monthly_inr || 0,                    color: "#f59e0b" },
                                { name: "Charger Maint.",      value: roi.expenses?.charger_maintenance_monthly_inr || 0,       color: "#8b5cf6" },
                                { name: "Solar Maint.",        value: roi.expenses?.solar_maintenance_monthly_inr || 0,         color: "#a78bfa" },
                                { name: "Building Maint.",     value: roi.expenses?.building_maintenance_monthly_inr || 0,      color: "#c4b5fd" },
                                { name: "CMS / Software",      value: roi.expenses?.cms_monthly_inr || 0,                      color: "#0ea5e9" },
                                { name: "Insurance",           value: roi.expenses?.insurance_monthly_inr || 0,                 color: "#10b981" },
                                { name: "Internet & Cleaning", value: (roi.expenses?.internet_monthly_inr || 0) + (roi.expenses?.cleaning_monthly_inr || 0), color: "#6366f1" },
                                { name: "Land Lease",          value: roi.expenses?.land_lease_monthly_inr || 0,                color: "#ec4899" },
                              ].filter(d => d.value > 0);
                              const barHeight = Math.max(240, expenseChartData.length * 34);
                              return (
                                <div style={{ height: barHeight }}>
                                  <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={expenseChartData} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
                                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                      <XAxis type="number" tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                                      <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                                      <Tooltip formatter={(value) => formatCurrency(value)} />
                                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                        {expenseChartData.map((entry, index) => (
                                          <Cell key={index} fill={entry.color} />
                                        ))}
                                      </Bar>
                                    </BarChart>
                                  </ResponsiveContainer>
                                </div>
                              );
                            })()}
                          </CardContent>
                        </Card>
                      </div>

                      {/* Solar Yield Card — shown if solar is configured */}
                      {(() => {
                        const solar = project.roi_analysis?.detailed_analysis?.opex?.solar_savings;
                        const solarKwp = project.layout?.solar?.estimated_kWp;
                        if (!solarKwp) return null;
                        // Compute savings from kWp if detailed_analysis is unavailable (old docs / recalc mode)
                        const ELEC_RATE = 7.5; // ₹/kWh — matches backend default
                        const PEAK_HRS  = 4.5; // India average peak sun hours/day
                        const monthlySavingsInr = solar?.monthly_savings_inr
                          ?? Math.round(solarKwp * PEAK_HRS * 30 * ELEC_RATE);
                        const annualSavingsInr = solar?.annual_savings_inr
                          ?? Math.round(solarKwp * PEAK_HRS * 365 * ELEC_RATE);
                        const annualKwh = solar?.annual_kwh_generated
                          ?? Math.round(solarKwp * PEAK_HRS * 365);
                        return (
                          <Card className="border-[#83E8BA]/20 bg-[#83E8BA]/10">
                            <CardHeader className="pb-2">
                              <CardTitle className="text-base flex items-center gap-2 text-[#504136]">
                                <Sun className="w-4 h-4" /> Solar Yield & Savings
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="grid sm:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <p className="text-[#689689] mb-1">System Size</p>
                                  <p className="font-mono font-bold text-[#504136]">{solarKwp} kWp</p>
                                </div>
                                <div>
                                  <p className="text-[#689689] mb-1">Annual Generation</p>
                                  <p className="font-mono font-bold text-[#504136]">
                                    {(annualKwh / 1000).toFixed(1)} MWh
                                  </p>
                                </div>
                                <div>
                                  <p className="text-[#689689] mb-1">Monthly Savings</p>
                                  <p className="font-mono font-bold text-[#504136]">
                                    {formatCurrency(monthlySavingsInr)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-[#689689] mb-1">Annual Savings</p>
                                  <p className="font-mono font-bold text-[#504136]">
                                    {formatCurrency(annualSavingsInr)}
                                  </p>
                                </div>
                              </div>
                              <p className="text-xs text-[#689689] mt-3">
                                Based on {PEAK_HRS} peak sun hours/day (India average). Solar generation directly offsets electricity purchase cost for charging.
                              </p>
                            </CardContent>
                          </Card>
                        );
                      })()}
                      {/* 10-Year Cash Flow Projection */}
                      {(() => {
                        const annualNetProfit = (roi.profit?.monthly_net_inr || 0) * 12;
                        const paybackYears = roi.roi?.payback_years || 0;
                        // CAPEX: prefer cost_estimate total; fall back to deriving from payback
                        const totalCapex =
                          project.cost_estimate?.total?.recommended_cost ||
                          (paybackYears > 0 && annualNetProfit > 0
                            ? Math.round(paybackYears * annualNetProfit)
                            : 0);
                        if (!totalCapex || annualNetProfit === 0) return null;

                        const cashflowData = [{ year: "Yr 0", annualNetProfit: 0, cumulativeCashflow: -totalCapex }];
                        let cumulative = -totalCapex;
                        for (let y = 1; y <= 10; y++) {
                          cumulative += annualNetProfit;
                          cashflowData.push({
                            year: `Yr ${y}`,
                            annualNetProfit: Math.round(annualNetProfit),
                            cumulativeCashflow: Math.round(cumulative),
                          });
                        }
                        const paybackYearMark = paybackYears > 0 && paybackYears <= 10 ? `Yr ${Math.ceil(paybackYears)}` : null;
                        const lakhs = (v) => `₹${(Math.abs(v) / 100000).toFixed(0)}L`;

                        return (
                          <Card className="border-[#B2E6D4] bg-white">
                            <CardHeader className="pb-3">
                              <div className="flex items-center justify-between">
                                <CardTitle className="text-base flex items-center gap-2 text-[#504136]">
                                  <div className="w-7 h-7 bg-[#83E8BA]/10 rounded-md flex items-center justify-center">
                                    <TrendingUp className="w-4 h-4 text-[#689689]" />
                                  </div>
                                  10-Year Cash Flow Projection
                                </CardTitle>
                                {paybackYears > 0 && (
                                  <div className="text-right">
                                    <p className="text-xs text-[#689689] font-medium">Payback Period</p>
                                    <p className="text-xl font-bold font-mono text-[#504136] leading-tight">{paybackYears.toFixed(1)} yrs</p>
                                  </div>
                                )}
                              </div>
                              <p className="text-xs text-[#4a7068] mt-1">
                                Annual net profit (bars) · cumulative cash flow (line) · break-even where line crosses zero
                              </p>
                            </CardHeader>
                            <CardContent>
                              <div className="h-72">
                                <ResponsiveContainer width="100%" height="100%">
                                  <ComposedChart data={cashflowData} margin={{ top: 10, right: 60, bottom: 4, left: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                                    <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                                    <YAxis
                                      yAxisId="bar"
                                      tickFormatter={lakhs}
                                      tick={{ fontSize: 10, fill: "#64748b" }}
                                      axisLine={false}
                                      tickLine={false}
                                      width={52}
                                    />
                                    <YAxis
                                      yAxisId="line"
                                      orientation="right"
                                      tickFormatter={lakhs}
                                      tick={{ fontSize: 10, fill: "#64748b" }}
                                      axisLine={false}
                                      tickLine={false}
                                      width={56}
                                    />
                                    <Tooltip
                                      formatter={(value, name) => [
                                        `${value < 0 ? "−" : ""}${formatCurrency(Math.abs(value))}`,
                                        name,
                                      ]}
                                      contentStyle={{ borderRadius: 8, border: "1px solid #B2E6D4", fontSize: 12 }}
                                    />
                                    {/* Break-even line */}
                                    <ReferenceLine yAxisId="line" y={0} stroke="#ef4444" strokeDasharray="5 3" strokeWidth={1.5} />
                                    {/* Payback year marker */}
                                    {paybackYearMark && (
                                      <ReferenceLine
                                        yAxisId="bar"
                                        x={paybackYearMark}
                                        stroke="#689689"
                                        strokeDasharray="5 3"
                                        strokeWidth={1.5}
                                        label={{ value: "Payback", position: "insideTopRight", fontSize: 10, fill: "#689689" }}
                                      />
                                    )}
                                    <Bar
                                      yAxisId="bar"
                                      dataKey="annualNetProfit"
                                      name="Annual Net Profit"
                                      fill="#83E8BA"
                                      radius={[3, 3, 0, 0]}
                                      maxBarSize={36}
                                    />
                                    <Line
                                      yAxisId="line"
                                      type="monotone"
                                      dataKey="cumulativeCashflow"
                                      name="Cumulative Cash Flow"
                                      stroke="#504136"
                                      strokeWidth={2.5}
                                      dot={{ fill: "#504136", r: 3, strokeWidth: 0 }}
                                      activeDot={{ r: 5 }}
                                    />
                                  </ComposedChart>
                                </ResponsiveContainer>
                              </div>
                              {/* Legend */}
                              <div className="flex flex-wrap items-center gap-5 mt-3 px-1">
                                <div className="flex items-center gap-1.5">
                                  <div className="w-3 h-3 rounded-sm bg-[#83E8BA]" />
                                  <span className="text-xs text-[#4a7068]">Annual Net Profit</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <div className="w-5 h-[2.5px] bg-[#504136] rounded" />
                                  <span className="text-xs text-[#4a7068]">Cumulative Cash Flow</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <div className="w-5 border-t-2 border-dashed border-red-400" />
                                  <span className="text-xs text-[#4a7068]">Break-even (₹0)</span>
                                </div>
                                {paybackYearMark && (
                                  <div className="flex items-center gap-1.5">
                                    <div className="w-5 border-t-2 border-dashed border-[#689689]" />
                                    <span className="text-xs text-[#4a7068]">Payback Year</span>
                                  </div>
                                )}
                              </div>
                              {/* Summary row */}
                              <div className="grid grid-cols-3 gap-3 mt-4">
                                <div className="bg-[#edf8f4] rounded-lg px-3 py-2 text-center">
                                  <p className="text-xs text-[#689689] mb-0.5">CAPEX Invested</p>
                                  <p className="font-mono font-semibold text-[#504136] text-sm">{formatCurrency(totalCapex)}</p>
                                </div>
                                <div className="bg-[#edf8f4] rounded-lg px-3 py-2 text-center">
                                  <p className="text-xs text-[#689689] mb-0.5">Annual Net Profit</p>
                                  <p className="font-mono font-semibold text-[#504136] text-sm">{formatCurrency(annualNetProfit)}</p>
                                </div>
                                <div className="bg-[#edf8f4] rounded-lg px-3 py-2 text-center">
                                  <p className="text-xs text-[#689689] mb-0.5">10-Year Net Return</p>
                                  <p className={`font-mono font-semibold text-sm ${cashflowData[10]?.cumulativeCashflow >= 0 ? "text-[#689689]" : "text-red-500"}`}>
                                    {formatCurrency(cashflowData[10]?.cumulativeCashflow || 0)}
                                  </p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })()}

                    </>
                  );
                })()}
              </>
            ) : (
              <Card className="border-[#B2E6D4] border-dashed bg-[#edf8f4]">
                <CardContent className="p-12 text-center">
                  <TrendingUp className="w-12 h-12 text-[#4a7068] mx-auto mb-4" />
                  <h3 className="font-semibold text-[#504136] mb-2">No ROI Analysis</h3>
                  <p className="text-[#4a7068] mb-4">Run the analysis to calculate ROI</p>
                </CardContent>
              </Card>
            )}
            </motion.div>
          </TabsContent>


          {/* Government Schemes Tab */}
          <TabsContent value="schemes">
            <motion.div key="schemes" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-6">
            {/* AI Subsidy Calculator */}
            <Card className="border-0 bg-gradient-to-br from-emerald-600 to-teal-700 text-white shadow-lg overflow-hidden relative">
              <div className="absolute right-0 top-0 w-40 h-40 bg-white/5 rounded-full -translate-y-16 translate-x-16" />
              <CardHeader className="pb-2 relative">
                <CardTitle className="text-base flex items-center gap-2">
                  <div className="w-7 h-7 bg-white/20 rounded-md flex items-center justify-center">
                    <IndianRupee className="w-4 h-4" />
                  </div>
                  AI Subsidy Calculator
                </CardTitle>
                <p className="text-emerald-100 text-xs mt-1">Calculate eligible grants and net CAPEX using Claude AI</p>
              </CardHeader>
              <CardContent className="relative">
                {project?.subsidy_analysis ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-white/10 rounded-xl p-3">
                        <p className="text-xs opacity-70 uppercase tracking-widest mb-1">Total Subsidy</p>
                        <p className="text-xl font-bold">{formatCurrency(project.subsidy_analysis.total_subsidy_inr)}</p>
                      </div>
                      <div className="bg-white/10 rounded-xl p-3">
                        <p className="text-xs opacity-70 uppercase tracking-widest mb-1">Net CAPEX</p>
                        <p className="text-xl font-bold">{formatCurrency(project.subsidy_analysis.net_capex_inr)}</p>
                      </div>
                      <div className="bg-white/10 rounded-xl p-3">
                        <p className="text-xs opacity-70 uppercase tracking-widest mb-1">Tax Benefits</p>
                        <p className="text-xl font-bold">{formatCurrency(project.subsidy_analysis.tax_benefits_inr)}</p>
                      </div>
                    </div>
                    {project.subsidy_analysis.notes && (
                      <p className="text-xs text-emerald-100 bg-white/10 rounded-lg p-3">{project.subsidy_analysis.notes}</p>
                    )}
                    <Button variant="outline" size="sm" className="text-white border-white/30 hover:bg-white/10 hover:text-white"
                      disabled={subsidyLoading} onClick={runSubsidyCalc}>
                      {subsidyLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                      Recalculate
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-emerald-100">
                      {project?.cost_estimate ? "Run AI analysis to find eligible subsidies" : "Complete cost estimation first"}
                    </p>
                    <Button size="sm" variant="outline" className="text-white border-white/30 hover:bg-white/10 hover:text-white"
                      disabled={subsidyLoading || !project?.cost_estimate} onClick={runSubsidyCalc}>
                      {subsidyLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Zap className="w-3 h-3 mr-1" />}
                      {subsidyLoading ? "Analyzing..." : "Run Analysis"}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Eligible Schemes from AI */}
            {project?.subsidy_analysis?.eligible_schemes?.length > 0 && (
              <Card className="border-[#B2E6D4] bg-white">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2 text-[#504136]">
                    <div className="w-7 h-7 bg-[#83E8BA]/10 rounded-md flex items-center justify-center">
                      <CheckSquare className="w-4 h-4 text-[#83E8BA]" />
                    </div>
                    Eligible Schemes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {project.subsidy_analysis.eligible_schemes.map((scheme, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 bg-[#edf8f4] rounded-xl border border-[#B2E6D4]">
                        <div className="w-8 h-8 bg-[#83E8BA]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <IndianRupee className="w-4 h-4 text-[#83E8BA]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-semibold text-[#504136] text-sm">{scheme.name}</p>
                            <span className="text-sm font-bold text-[#689689] flex-shrink-0">{formatCurrency(scheme.amount_inr)}</span>
                          </div>
                          <p className="text-xs text-[#4a7068] mt-0.5">{scheme.authority} · {scheme.validity}</p>
                          <p className="text-xs text-[#4a7068] mt-1">{scheme.eligibility_basis}</p>
                          {scheme.application_url && (
                            <a href={scheme.application_url} target="_blank" rel="noreferrer" className="text-xs text-[#689689] hover:underline mt-1 block">
                              Apply ↗
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {project.subsidy_analysis.application_priority?.length > 0 && (
                    <div className="mt-4 p-3 bg-[#c0b9ac]/10 rounded-xl border border-[#c0b9ac]/20">
                      <p className="text-xs font-semibold text-[#A49E8D] uppercase tracking-widest mb-2">Application Priority</p>
                      <ol className="space-y-1">
                        {project.subsidy_analysis.application_priority.map((p, i) => (
                          <li key={i} className="text-xs text-[#4a7068] flex items-center gap-2">
                            <span className="w-5 h-5 bg-[#c0b9ac]/20 rounded-full flex items-center justify-center text-[#A49E8D] font-bold flex-shrink-0">{i + 1}</span>
                            {p}
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {(() => {
              const state = project.site_inputs?.state;
              const STATE_INFO = {
                "Maharashtra": { discom: "MSEDCL / Adani Electricity", tariff: "₹6–8/kWh (EV charging tariff)", subsidy: "Up to ₹10L capital subsidy per station under Maharashtra EV Policy 2021", policy: "Maharashtra EV Policy 2021" },
                "Delhi": { discom: "BSES / Tata Power Delhi", tariff: "₹4.5–5.5/kWh (subsidised EV tariff)", subsidy: "50% subsidy on charging equipment cost (up to ₹6,000/kW)", policy: "Delhi EV Policy 2020" },
                "Karnataka": { discom: "BESCOM / HESCOM", tariff: "₹5–6/kWh", subsidy: "Capital subsidy for public charging stations under Karnataka EV Policy 2021", policy: "Karnataka EV Policy 2021" },
                "Tamil Nadu": { discom: "TANGEDCO", tariff: "₹5.5–7/kWh", subsidy: "Capital subsidy and land allocation support under Tamil Nadu EV Policy 2023", policy: "Tamil Nadu EV Policy 2023" },
                "Gujarat": { discom: "UGVCL / MGVCL / PGVCL / DGVCL", tariff: "₹5–6/kWh (Time-of-Day pricing available)", subsidy: "25% capital subsidy for fast charging stations under Gujarat EV Policy 2021", policy: "Gujarat EV Policy 2021" },
                "Rajasthan": { discom: "JVVNL / AVVNL / JdVVNL", tariff: "₹6–7/kWh", subsidy: "Capital subsidy up to ₹10L per station under Rajasthan EV Policy 2022", policy: "Rajasthan EV Policy 2022" },
                "Uttar Pradesh": { discom: "PVVNL / DVVNL / MVVNL / PuVVNL", tariff: "₹5–6.5/kWh", subsidy: "Land allocation and 15% capital subsidy under UP EV Manufacturing & Mobility Policy 2022", policy: "UP EV Policy 2022" },
                "Telangana": { discom: "TSSPDCL / TSNPDCL", tariff: "₹5.5–7/kWh", subsidy: "Plug-and-play infrastructure support under Telangana EV Policy 2020", policy: "Telangana EV Policy 2020" },
                "Andhra Pradesh": { discom: "APEPDCL / APSPDCL", tariff: "₹5–6/kWh", subsidy: "Capital subsidy and power connection support under AP EV Policy 2023", policy: "AP EV Policy 2023" },
                "Kerala": { discom: "KSEB", tariff: "₹4.5–6/kWh (Time-of-Use pricing)", subsidy: "Interest subsidy on loans and land support under Kerala EV Policy 2022", policy: "Kerala EV Policy 2022" },
                "Haryana": { discom: "DHBVN / UHBVN", tariff: "₹5.5–7/kWh", subsidy: "Capital subsidy for public charging infrastructure under Haryana EV Policy 2022", policy: "Haryana EV Policy 2022" },
                "Madhya Pradesh": { discom: "MPPKVVCL / MPMKVVCL / MPWZVVCL", tariff: "₹6–7.5/kWh", subsidy: "Capital subsidy and land support under MP EV Policy 2019", policy: "MP EV Policy 2019" },
                "Punjab": { discom: "PSPCL", tariff: "₹5–6.5/kWh", subsidy: "Capital subsidy under Punjab EV Policy 2022", policy: "Punjab EV Policy 2022" },
              };
              const stateData = state ? STATE_INFO[state] : null;
              return (
                <>
                  {/* ── State-specific hero card ──────────────────────── */}
                  {state && (
                    <Card className={`border-0 overflow-hidden relative ${stateData ? "bg-gradient-to-br from-[#689689] to-[#4a7068] text-white shadow-lg shadow-[#B2E6D4]" : "bg-gradient-to-br from-[#4a7068] to-[#504136] text-white shadow-lg"}`}>
                      <div className="absolute right-0 top-0 w-40 h-40 bg-white/5 rounded-full -translate-y-16 translate-x-16" />
                      <CardHeader className="pb-3 relative">
                        <CardTitle className="text-base flex items-center gap-2">
                          <div className="w-7 h-7 bg-white/20 rounded-md flex items-center justify-center">
                            <MapPin className="w-4 h-4" />
                          </div>
                          {state} — State-Specific Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="relative">
                        {stateData ? (
                          <div className="grid sm:grid-cols-2 gap-3">
                            <div className="bg-white/10 rounded-xl p-3">
                              <p className="text-xs font-medium uppercase tracking-widest opacity-70 mb-1">DISCOM</p>
                              <p className="font-semibold text-sm">{stateData.discom}</p>
                            </div>
                            <div className="bg-white/10 rounded-xl p-3">
                              <p className="text-xs font-medium uppercase tracking-widest opacity-70 mb-1">EV Charging Tariff</p>
                              <p className="font-semibold text-sm text-emerald-200">{stateData.tariff}</p>
                            </div>
                            <div className="bg-white/10 rounded-xl p-3 sm:col-span-2">
                              <p className="text-xs font-medium uppercase tracking-widest opacity-70 mb-1">Capital Subsidy / Incentive</p>
                              <p className="font-semibold text-sm">{stateData.subsidy}</p>
                            </div>
                            <div className="bg-white/10 rounded-xl p-3 sm:col-span-2">
                              <p className="text-xs font-medium uppercase tracking-widest opacity-70 mb-1">Policy Reference</p>
                              <p className="font-semibold text-sm text-yellow-200">{stateData.policy}</p>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm opacity-80">
                            State-specific tariff data for <strong>{state}</strong> is not yet in our database.
                            Please check your state's official EV policy portal and DISCOM website for the latest rates.
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* ── Central schemes ───────────────────────────────── */}
                  <Card className="border-[#B2E6D4] bg-white">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2 text-[#504136]">
                        <div className="w-7 h-7 bg-[#689689]/10 rounded-md flex items-center justify-center">
                          <Landmark className="w-4 h-4 text-[#689689]" />
                        </div>
                        Central Government Schemes
                      </CardTitle>
                      <CardDescription className="text-[#3d6b65] font-medium">National programs applicable across all states</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 gap-3">
                        {[
                          { icon: <Landmark className="w-4 h-4 text-[#689689]" />, bg: "bg-[#689689]/10", title: "PM E-DRIVE", desc: "Scheme portal and operational guidelines for EV ecosystem support.", link: "https://pmedrive.heavyindustries.gov.in/", linkLabel: "Ministry of Heavy Industries – PM E-DRIVE" },
                          { icon: <Landmark className="w-4 h-4 text-[#83E8BA]" />, bg: "bg-[#83E8BA]/10", title: "EV Mitra / SBI Financing", desc: 'Financing support product "EV Mitra" by SBI for charging infrastructure.', link: "https://sbi.co.in/web/business/sme/sme-loans/ev-mitra", linkLabel: "SBI – EV Mitra" },
                          { icon: <IndianRupee className="w-4 h-4 text-[#c0b9ac]" />, bg: "bg-[#c0b9ac]/10", title: "MoP EV Charging Guidelines", desc: "Guidelines helping DISCOMs define EV charging tariffs and operations.", link: "https://powermin.gov.in/sites/default/files/uploads/Final_revised_Guidelines_on_Electric_Vehicle_Charging_Infrastructure_for_EV.pdf", linkLabel: "Ministry of Power – Guidelines (PDF)" },
                          { icon: <IndianRupee className="w-4 h-4 text-[#A49E8D]" />, bg: "bg-[#A49E8D]/10", title: "MSME Benefits", desc: "If operator qualifies as MSME, explore loan and subsidy programs.", link: "https://msme.gov.in/", linkLabel: "Ministry of MSME" },
                          { icon: <Landmark className="w-4 h-4 text-[#4a7068]" />, bg: "bg-[#edf8f4]", title: "SIDBI Financing", desc: "SIDBI financing programs supporting EV ecosystem businesses.", link: "https://www.sidbi.in/", linkLabel: "SIDBI" },
                          { icon: <Sun className="w-4 h-4 text-[#c0b9ac]" />, bg: "bg-[#c0b9ac]/10", title: "Solar / MNRE Incentives", desc: "Rooftop solar subsidies and state net-metering policies.", link: "https://mnre.gov.in/", linkLabel: "MNRE" },
                        ].map((item, i) => (
                          <div key={i} className="flex items-start gap-3 bg-[#edf8f4] hover:bg-[#B2E6D4]/40 transition-colors rounded-xl p-4 border border-[#B2E6D4]">
                            <div className={`w-8 h-8 ${item.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                              {item.icon}
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-[#504136] text-sm mb-0.5">{item.title}</p>
                              <p className="text-xs text-[#4a7068] mb-1.5">{item.desc}</p>
                              <a href={item.link} target="_blank" rel="noreferrer" className="text-xs text-[#689689] hover:underline font-medium">
                                {item.linkLabel} ↗
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <div className="border-l-4 border-[#c0b9ac]/50 bg-[#c0b9ac]/10 rounded-r-xl px-4 py-3">
                    <p className="text-sm text-[#4a7068]">
                      <strong className="text-[#A49E8D]">Note:</strong> Incentives and eligibility vary by state, DISCOM, and applicant category.
                      {!state && " Add your project's state to see state-specific tariffs and subsidies."}
                      {" "}Use the links above as authoritative starting points and verify with local authorities.
                    </p>
                  </div>
                </>
              );
            })()}
            </motion.div>
          </TabsContent>


          {/* Compliance Tab */}
          <TabsContent value="compliance">
            <motion.div key="compliance" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-6">
            {project.compliance_checklist && (
              <div className="flex justify-end">
                <Button variant="outline" size="sm" disabled={stepsRunning["generate-compliance"]}
                  onClick={() => runStep("generate-compliance", "Compliance checklist")}>
                  {stepsRunning["generate-compliance"] ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                  Re-generate
                </Button>
              </div>
            )}
            {project.compliance_checklist ? (
              <>
                {/* ── Notes / source banner ───────────────────────────── */}
                {project.compliance_checklist.notes && (
                  <div className="flex items-start gap-3 bg-[#689689]/10 border border-[#689689]/20 rounded-xl px-4 py-3 text-sm text-[#504136]">
                    <FileText className="w-4 h-4 mt-0.5 shrink-0 text-[#689689]" />
                    <span>{project.compliance_checklist.notes}</span>
                  </div>
                )}

                {/* ── Compliance KPI Row ──────────────────────────────── */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="border-0 bg-gradient-to-br from-red-500 to-red-700 text-white shadow-lg shadow-red-100 overflow-hidden relative">
                    <div className="absolute right-0 top-0 w-24 h-24 bg-white/5 rounded-full -translate-y-8 translate-x-8" />
                    <CardContent className="p-5 relative">
                      <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center mb-3">
                        <Shield className="w-5 h-5" />
                      </div>
                      <p className="text-red-100 text-xs font-medium uppercase tracking-widest mb-1">Mandatory</p>
                      <p className="text-4xl font-bold">{project.compliance_checklist.total_mandatory}</p>
                      <p className="text-red-200 text-xs mt-1">must comply before commissioning</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 bg-gradient-to-br from-[#689689] to-[#83E8BA] text-white shadow-lg shadow-[#B2E6D4] overflow-hidden relative">
                    <div className="absolute right-0 top-0 w-24 h-24 bg-white/5 rounded-full -translate-y-8 translate-x-8" />
                    <CardContent className="p-5 relative">
                      <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center mb-3">
                        <CheckCircle2 className="w-5 h-5" />
                      </div>
                      <p className="text-[#B2E6D4] text-xs font-medium uppercase tracking-widest mb-1">Recommended</p>
                      <p className="text-4xl font-bold">{project.compliance_checklist.total_recommended}</p>
                      <p className="text-[#B2E6D4] text-xs mt-1">best-practice improvements</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 bg-gradient-to-br from-[#4a7068] to-[#504136] text-white shadow-lg shadow-[#B2E6D4] overflow-hidden relative">
                    <div className="absolute right-0 top-0 w-24 h-24 bg-white/5 rounded-full -translate-y-8 translate-x-8" />
                    <CardContent className="p-5 relative">
                      <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center mb-3">
                        <FileText className="w-5 h-5" />
                      </div>
                      <p className="text-[#B2E6D4] text-xs font-medium uppercase tracking-widest mb-1">Total Items</p>
                      <p className="text-4xl font-bold">{(project.compliance_checklist.total_mandatory || 0) + (project.compliance_checklist.total_recommended || 0)}</p>
                      <p className="text-[#B2E6D4] text-xs mt-1">{project.site_inputs?.state || "India"} regulations</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 bg-gradient-to-br from-violet-500 to-violet-700 text-white shadow-lg shadow-violet-100 overflow-hidden relative">
                    <div className="absolute right-0 top-0 w-24 h-24 bg-white/5 rounded-full -translate-y-8 translate-x-8" />
                    <CardContent className="p-5 relative">
                      <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center mb-3">
                        <Shield className="w-5 h-5" />
                      </div>
                      <p className="text-violet-100 text-xs font-medium uppercase tracking-widest mb-1">Categories</p>
                      <p className="text-4xl font-bold">{project.compliance_checklist.compliance_checklist?.length || 0}</p>
                      <p className="text-violet-200 text-xs mt-1">regulatory domains covered</p>
                    </CardContent>
                  </Card>
                </div>

                <div className="space-y-4">
                  {project.compliance_checklist.compliance_checklist?.map((category, i) => (
                    <Card key={i} className="border-[#B2E6D4] bg-white">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2 text-[#504136]">
                          <div className="w-7 h-7 bg-[#edf8f4] rounded-md flex items-center justify-center text-base leading-none">
                            {(() => {
                              const c = (category.category || "").toLowerCase();
                              if (c.includes("electrical")) return "⚡";
                              if (c.includes("equipment") || c.includes("certification") || c.includes("standard")) return "🏷️";
                              if (c.includes("fire") || c.includes("physical safety")) return "🔥";
                              if (c.includes("permit") || c.includes("approval")) return "🧾";
                              if (c.includes("signage") || c.includes("infrastructure")) return "🪧";
                              if (c.includes("access") || c.includes("disability")) return "♿";
                              if (c.includes("environment") || c.includes("green")) return "🌿";
                              if (c.includes("connect") || c.includes("protocol") || c.includes("ocpp")) return "🔌";
                              if (c.includes("cyber") || c.includes("security") || c.includes("data")) return "🔒";
                              if (c.includes("meter") || c.includes("tariff") || c.includes("billing")) return "📊";
                              if (c.includes("interop") || c.includes("discover") || c.includes("roaming")) return "🌐";
                              if (c.includes("land") || c.includes("building") || c.includes("zoning")) return "🏗️";
                              return "✅";
                            })()}
                          </div>
                          <span>{category.category}</span>
                          <span className="ml-auto text-xs text-[#4a7068] font-normal">
                            {category.items?.filter(it => it.priority === "mandatory").length || 0} mandatory
                            {" · "}
                            {category.items?.filter(it => it.priority !== "mandatory").length || 0} recommended
                          </span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {category.items?.map((item, j) => (
                            <div
                              key={j}
                              className={`flex items-start gap-3 rounded-xl px-4 py-3 ${
                                item.priority === "mandatory"
                                  ? "bg-red-500/10 border border-red-500/20"
                                  : "bg-[#edf8f4] border border-[#B2E6D4]"
                              }`}
                            >
                              <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold ${
                                item.priority === "mandatory" ? "bg-red-500 text-white" : "bg-[#83E8BA] text-white"
                              }`}>
                                {item.priority === "mandatory" ? "!" : "✓"}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-[#504136] text-sm">{item.requirement}</p>
                                {item.description && (
                                  <p className="text-xs text-[#4a7068] mt-1">{item.description}</p>
                                )}
                                {item.standard && (
                                  <p className="text-xs text-[#689689] mt-1 font-medium">📋 {item.standard}</p>
                                )}
                              </div>
                              <span className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full ${
                                item.priority === "mandatory"
                                  ? "bg-red-500/20 text-red-600"
                                  : "bg-[#B2E6D4] text-[#4a7068]"
                              }`}>
                                {item.priority}
                              </span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Reference Sources */}
                <Card className="border-[#B2E6D4] bg-[#edf8f4]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 text-[#504136] font-semibold">
                      <FileText className="w-4 h-4" />
                      Reference Regulations & Standards
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-2 text-xs text-[#4a7068]">
                      {[
                        { label: "MoP EVCI Guidelines 2022", desc: "Ministry of Power consolidated guidelines for EV charging infrastructure" },
                        { label: "CEA Regulations 2010 (Amended 2023)", desc: "Central Electricity Authority safety regulations for electrical installations" },
                        { label: "BIS IS 17017 Series", desc: "Indian Standard for EVSE — AC chargers (Part 1&2), DC chargers (Part 23&24), low-power (Part 25)" },
                        { label: "ARAI AIS-138", desc: "Automotive Industry Standard for EV charger type-approval (Part 1: AC, Part 2: DC)" },
                        { label: "MoHUA MBBL 2016", desc: "Model Building Bye-Laws mandating 20% EV-ready parking in new constructions" },
                        { label: "SERC / State EV Tariff Orders", desc: "State-specific concessional EV tariff — check KERC, TNERC, MERC, MSEDCL, etc." },
                        { label: "CERT-In Cybersecurity Directives", desc: "IT Act 2000 directions on cybersecurity for EV charging networks" },
                        { label: "NBC 2016 (Part 4)", desc: "National Building Code fire safety provisions applicable to charging stations" },
                        { label: "BEE Evyatra Portal", desc: "Mandatory registration platform for all public EV charging stations in India" },
                        { label: "Rights of Persons with Disabilities Act 2016", desc: "Accessibility requirements for public EV charging bays" },
                      ].map((src, i) => (
                        <div key={i} className="flex items-start gap-2 bg-white rounded-lg border border-[#B2E6D4] px-3 py-2">
                          <span className="text-[#689689] mt-0.5 shrink-0">▪</span>
                          <div>
                            <p className="font-semibold text-[#504136]">{src.label}</p>
                            <p className="text-[#4a7068]">{src.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="border-[#B2E6D4] border-dashed bg-[#edf8f4]">
                <CardContent className="p-12 text-center">
                  <Shield className="w-12 h-12 text-[#4a7068] mx-auto mb-4" />
                  <h3 className="font-semibold text-[#504136] mb-2">No Compliance Checklist</h3>
                  <p className="text-[#4a7068] mb-4">Run the analysis to generate compliance checklist</p>
                </CardContent>
              </Card>
            )}
            </motion.div>
          </TabsContent>

          {/* Location Intelligence Tab */}
          <TabsContent value="location">
            <motion.div key="location" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-6">
            <Card className="border-0 bg-gradient-to-br from-[#689689] to-[#4a7068] text-white shadow-lg overflow-hidden relative">
              <div className="absolute right-0 top-0 w-40 h-40 bg-white/5 rounded-full -translate-y-16 translate-x-16" />
              <CardHeader className="pb-2 relative">
                <CardTitle className="text-base flex items-center gap-2">
                  <div className="w-7 h-7 bg-white/20 rounded-md flex items-center justify-center">
                    <Navigation className="w-4 h-4" />
                  </div>
                  Location Intelligence Engine
                </CardTitle>
                <p className="text-[#B2E6D4] text-xs mt-1">
                  Nominatim geocoding + Claude AI market knowledge → Demand Readiness Index
                  {!project?.site_inputs?.pin_code && " · Add a PIN code in project settings for precise location"}
                </p>
              </CardHeader>
              <CardContent className="relative">
                {project?.location_intelligence ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <div className={`text-5xl font-bold ${
                          project.location_intelligence.demand_readiness_index >= 71 ? "text-green-300"
                          : project.location_intelligence.demand_readiness_index >= 41 ? "text-yellow-300"
                          : "text-red-300"
                        }`}>
                          {project.location_intelligence.demand_readiness_index}
                        </div>
                        <div className="text-xs text-[#B2E6D4] mt-1">Demand Index</div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 flex-1">
                        <div className="bg-white/10 rounded-xl p-2 text-center">
                          <div className="text-sm font-bold">{project.location_intelligence.location_tier}</div>
                          <div className="text-xs opacity-70">Tier</div>
                        </div>
                        <div className="bg-white/10 rounded-xl p-2 text-center">
                          <div className="text-sm font-bold">
                            {project.location_intelligence.competitor_count ||
                              (project.location_intelligence.competitor_stations?.length || 0)}
                          </div>
                          <div className="text-xs opacity-70">Nearby</div>
                        </div>
                        <div className="bg-white/10 rounded-xl p-2 text-center">
                          <div className="text-sm font-bold">{project.location_intelligence.supply_gap}</div>
                          <div className="text-xs opacity-70">Gap</div>
                        </div>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="text-white border-white/30 hover:bg-white/10 hover:text-white"
                      disabled={locationLoading} onClick={runLocationAnalysis}>
                      {locationLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                      Re-analyze
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-[#B2E6D4]">Analyze demand and competition for this location</p>
                    <Button size="sm" variant="outline" className="text-white border-white/30 hover:bg-white/10 hover:text-white"
                      disabled={locationLoading} onClick={runLocationAnalysis}>
                      {locationLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Zap className="w-3 h-3 mr-1" />}
                      {locationLoading ? "Analyzing..." : "Run Analysis"}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {project?.location_intelligence && (
              <>
                {/* ── Interactive Competition Map ──────────────────────────── */}
                <Card className="border-[#B2E6D4] bg-white overflow-hidden">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 text-[#504136]">
                      <Navigation className="w-4 h-4 text-[#689689]" />
                      Competition Map
                      <span className="ml-auto text-xs font-normal text-[#4a7068]">
                        {project.location_intelligence.competitor_count ||
                        (project.location_intelligence.competitor_stations?.length || 0)} stations within 5 km
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {!project.location_intelligence.lat || !project.location_intelligence.lng ? (
                      <div className="flex flex-col items-center justify-center gap-4 py-10 px-6 bg-slate-50">
                        <MapPin className="w-8 h-8 text-slate-400" />
                        {project.site_inputs?.pin_code ? (
                          <div className="text-center">
                            <p className="font-semibold text-slate-700 mb-1">Ready to map your location</p>
                            <p className="text-sm text-slate-500">
                              Using PIN code <span className="font-mono font-semibold text-slate-700">{project.site_inputs.pin_code}</span> from your project. Click <strong>Run Analysis</strong> above to generate the map.
                            </p>
                          </div>
                        ) : (
                          <>
                            <div className="text-center">
                              <p className="font-semibold text-slate-700 mb-1">PIN code needed for map</p>
                              <p className="text-sm text-slate-500">Enter your site's 6-digit PIN code to geocode the location</p>
                            </div>
                            <div className="flex gap-2 w-full max-w-xs">
                              <input
                                type="text"
                                maxLength={6}
                                placeholder="e.g. 560001"
                                value={pinInput}
                                onChange={e => setPinInput(e.target.value.replace(/\D/g, ""))}
                                className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#689689]"
                              />
                              <Button size="sm" disabled={locationLoading || pinInput.length !== 6} onClick={savePinAndAnalyze}
                                className="bg-[#689689] hover:bg-[#4a7068] text-white">
                                {locationLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Analyze"}
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <Suspense fallback={
                        <div className="h-64 flex items-center justify-center bg-[#edf8f4] text-[#4a7068] text-sm">
                          Loading map…
                        </div>
                      }>
                        <CompetitionMap
                          lat={project.location_intelligence.lat}
                          lng={project.location_intelligence.lng}
                          competitor_stations={project.location_intelligence.competitor_stations || []}
                          demand_readiness_index={project.location_intelligence.demand_readiness_index}
                          supply_gap={project.location_intelligence.supply_gap}
                        />
                      </Suspense>
                    )}
                  </CardContent>
                </Card>

                {/* Score Breakdown */}
                <div className="grid sm:grid-cols-4 gap-4">
                  {Object.entries(project.location_intelligence.demand_score_breakdown || {}).map(([key, val]) => (
                    <Card key={key} className="border-[#B2E6D4] bg-white">
                      <CardContent className="p-4 text-center">
                        <div className={`text-3xl font-bold mb-1 ${
                          val >= 20 ? "text-[#689689]" : val >= 12 ? "text-[#c0b9ac]" : "text-red-500"
                        }`}>{val}<span className="text-sm text-[#4a7068]">/25</span></div>
                        <p className="text-xs text-[#4a7068] capitalize">{key.replace(/_/g, " ")}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Opportunity Summary */}
                {project.location_intelligence.opportunity_summary && (
                  <Card className="border-[#B2E6D4] bg-white">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2 text-[#504136]">
                        <Star className="w-4 h-4 text-[#c0b9ac]" />
                        Opportunity Summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-[#4a7068]">{project.location_intelligence.opportunity_summary}</p>
                      {project.location_intelligence.recommended_charger_mix && (
                        <p className="text-sm text-[#4a7068] mt-2 font-medium">Recommended mix: {project.location_intelligence.recommended_charger_mix}</p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Demand Drivers & Risk Factors */}
                <div className="grid lg:grid-cols-2 gap-4">
                  {project.location_intelligence.demand_drivers?.length > 0 && (
                    <Card className="border-[#B2E6D4] bg-white">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2 text-[#504136]">
                          <TrendingUp className="w-4 h-4 text-[#83E8BA]" />
                          Demand Drivers
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2">
                          {project.location_intelligence.demand_drivers.map((d, i) => (
                            <span key={i} className="px-2 py-1 bg-[#83E8BA]/10 text-[#689689] rounded-full text-xs font-medium border border-[#83E8BA]/20">{d}</span>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {project.location_intelligence.risk_factors?.length > 0 && (
                    <Card className="border-[#B2E6D4] bg-white">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2 text-[#504136]">
                          <AlertTriangle className="w-4 h-4 text-[#c0b9ac]" />
                          Risk Factors
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-1">
                          {project.location_intelligence.risk_factors.map((r, i) => (
                            <li key={i} className="text-xs text-[#4a7068] flex items-start gap-2">
                              <span className="text-[#c0b9ac] mt-0.5">▪</span>{r}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Competitor Stations */}
                {project.location_intelligence.competitor_stations?.length > 0 && (
                  <Card className="border-[#B2E6D4] bg-white">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2 text-[#504136]">
                        <Zap className="w-4 h-4 text-[#689689]" />
                        Nearby Charging Stations
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {project.location_intelligence.competitor_stations.map((s, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-[#edf8f4] rounded-lg border border-[#B2E6D4] text-sm">
                            <div>
                              <p className="font-medium text-[#504136]">{s.name}</p>
                              <p className="text-xs text-[#4a7068]">{s.operator}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium text-[#4a7068]">{s.distance_km} km</p>
                              <p className="text-xs text-[#4a7068]">{s.charger_count} chargers</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {!project?.location_intelligence && (
              <Card className="border-[#B2E6D4] border-dashed bg-[#edf8f4]">
                <CardContent className="p-12 text-center">
                  <Navigation className="w-12 h-12 text-[#4a7068] mx-auto mb-4" />
                  <h3 className="font-semibold text-[#504136] mb-2">No Location Analysis Yet</h3>
                  <p className="text-[#4a7068] text-sm">Click "Run Analysis" above to get demand intelligence for this location</p>
                </CardContent>
              </Card>
            )}
            </motion.div>
          </TabsContent>

          {/* Hardware Intelligence Tab */}
          <TabsContent value="hardware">
            <motion.div key="hardware" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-6">
            <Card className="border-0 bg-gradient-to-br from-[#4a7068] to-[#504136] text-white shadow-lg overflow-hidden relative">
              <div className="absolute right-0 top-0 w-40 h-40 bg-white/5 rounded-full -translate-y-16 translate-x-16" />
              <CardHeader className="pb-2 relative">
                <div className="flex items-start justify-between gap-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <div className="w-7 h-7 bg-white/20 rounded-md flex items-center justify-center">
                      <Cpu className="w-4 h-4" />
                    </div>
                    Hardware Intelligence & OEM Comparison
                  </CardTitle>
                  <Link
                    to="/charger-catalog"
                    className="flex-shrink-0 flex items-center gap-1.5 text-xs font-semibold bg-white/10 hover:bg-white/20 border border-white/20 text-white px-3 py-1.5 rounded-lg transition-colors"
                  >
                    <ExternalLink className="w-3 h-3" /> Full Catalog
                  </Link>
                </div>
                <p className="text-[#B2E6D4] text-xs mt-1">OEM database + Claude AI → charger recommendations & TCO</p>
              </CardHeader>
              <CardContent className="relative">
                {project?.hardware_recommendations ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-white/10 rounded-xl p-3">
                        <p className="text-xs opacity-70 uppercase tracking-widest mb-1">Hardware Cost</p>
                        <p className="text-xl font-bold">{formatCurrency(project.hardware_recommendations.total_hardware_cost_inr)}</p>
                      </div>
                      <div className="bg-white/10 rounded-xl p-3">
                        <p className="text-xs opacity-70 uppercase tracking-widest mb-1">10-Yr TCO</p>
                        <p className="text-xl font-bold">{formatCurrency(project.hardware_recommendations.tco_10yr_inr)}</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="text-white border-white/30 hover:bg-white/10 hover:text-white"
                      disabled={hardwareLoading} onClick={runHardwareRec}>
                      {hardwareLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                      Recalculate
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-[#B2E6D4]">
                      {project?.layout ? "Get OEM-specific hardware recommendations" : "Generate layout first"}
                    </p>
                    <Button size="sm" variant="outline" className="text-white border-white/30 hover:bg-white/10 hover:text-white"
                      disabled={hardwareLoading || !project?.layout} onClick={runHardwareRec}>
                      {hardwareLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Zap className="w-3 h-3 mr-1" />}
                      {hardwareLoading ? "Analyzing..." : "Get Recommendations"}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {project?.hardware_recommendations && (
              <>
                {/* Recommended Config Table */}
                {project.hardware_recommendations.recommended_config?.length > 0 && (
                  <Card className="border-[#B2E6D4] bg-white">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2 text-[#504136]">
                        <Package className="w-4 h-4 text-[#689689]" />
                        Recommended Configuration
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-[#B2E6D4]">
                              <th className="text-left py-2 px-2 text-xs text-[#504136] font-semibold">Brand / Model</th>
                              <th className="text-center py-2 px-2 text-xs text-[#504136] font-semibold">Power</th>
                              <th className="text-center py-2 px-2 text-xs text-[#504136] font-semibold">Qty</th>
                              <th className="text-right py-2 px-2 text-xs text-[#504136] font-semibold">Unit Price</th>
                              <th className="text-right py-2 px-2 text-xs text-[#504136] font-semibold">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {project.hardware_recommendations.recommended_config.map((item, i) => (
                              <tr key={i} className="border-b border-[#B2E6D4] hover:bg-[#edf8f4]">
                                <td className="py-2 px-2">
                                  <p className="font-medium text-[#504136]">{item.brand}</p>
                                  <p className="text-xs text-[#4a7068]">{item.model}</p>
                                </td>
                                <td className="py-2 px-2 text-center text-[#4a7068]">{item.power_kw} kW</td>
                                <td className="py-2 px-2 text-center font-mono font-bold text-[#504136]">{item.quantity}</td>
                                <td className="py-2 px-2 text-right text-[#4a7068]">{formatCurrency(item.unit_price_inr)}</td>
                                <td className="py-2 px-2 text-right font-semibold text-[#504136]">{formatCurrency(item.total_price_inr)}</td>
                              </tr>
                            ))}
                            <tr className="bg-[#edf8f4]">
                              <td colSpan={4} className="py-2 px-2 text-right font-semibold text-[#4a7068] text-sm">Total Hardware Cost</td>
                              <td className="py-2 px-2 text-right font-bold text-[#689689]">{formatCurrency(project.hardware_recommendations.total_hardware_cost_inr)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Connector Compatibility + Warranty + Procurement Tips */}
                <div className="grid lg:grid-cols-2 gap-4">
                  <Card className="border-[#B2E6D4] bg-white">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-[#504136]">Connector Compatibility</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(project.hardware_recommendations.connector_compatibility || {}).map(([k, v]) => (
                          <div key={k} className={`flex items-center gap-2 p-2 rounded-lg text-xs font-medium ${v ? "bg-[#83E8BA]/10 text-[#689689] border border-[#83E8BA]/20" : "bg-[#edf8f4] text-[#4a7068] border border-[#B2E6D4]"}`}>
                            {v ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                            {k.toUpperCase().replace(/_/g, " ")}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-[#B2E6D4] bg-white">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-[#504136]">Procurement Tips</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {project.hardware_recommendations.procurement_tips?.map((tip, i) => (
                          <li key={i} className="text-xs text-[#4a7068] flex items-start gap-2">
                            <span className="text-[#689689] mt-0.5 flex-shrink-0">▪</span>{tip}
                          </li>
                        ))}
                      </ul>
                      {project.hardware_recommendations.warranty_summary && (
                        <p className="text-xs text-[#4a7068] mt-3 p-2 bg-[#edf8f4] rounded-lg border border-[#B2E6D4]">
                          {project.hardware_recommendations.warranty_summary}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Alternatives */}
                {project.hardware_recommendations.alternatives?.length > 0 && (
                  <Card className="border-[#B2E6D4] bg-white">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2 text-[#504136]">
                        <SlidersHorizontal className="w-4 h-4 text-[#4a7068]" />
                        Alternative Options
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 gap-3">
                        {project.hardware_recommendations.alternatives.map((alt, i) => (
                          <div key={i} className="p-3 bg-[#edf8f4] rounded-xl border border-[#B2E6D4]">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <p className="font-semibold text-[#504136] text-sm">{alt.brand} {alt.model}</p>
                                <p className="text-xs text-[#4a7068]">{alt.power_kw} kW · {formatCurrency(alt.price_inr)}</p>
                              </div>
                            </div>
                            {alt.pros?.length > 0 && (
                              <div className="mb-1">
                                {alt.pros.map((p, j) => <p key={j} className="text-xs text-[#689689]">+ {p}</p>)}
                              </div>
                            )}
                            {alt.cons?.length > 0 && (
                              <div>
                                {alt.cons.map((c, j) => <p key={j} className="text-xs text-red-500">− {c}</p>)}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {!project?.hardware_recommendations && (
              <Card className="border-[#B2E6D4] border-dashed bg-[#edf8f4]">
                <CardContent className="p-12 text-center">
                  <Cpu className="w-12 h-12 text-[#4a7068] mx-auto mb-4" />
                  <h3 className="font-semibold text-[#504136] mb-2">No Hardware Recommendations Yet</h3>
                  <p className="text-[#4a7068] text-sm">
                    {project?.layout ? "Click \"Get Recommendations\" above to get OEM-specific suggestions" : "Generate the layout first, then come back for hardware recommendations"}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Charger Catalog CTA */}
            <div className="rounded-2xl overflow-hidden border border-[#B2E6D4] bg-gradient-to-r from-[#689689] to-[#504136] p-6 flex items-center justify-between gap-4">
              <div>
                <p className="text-white font-bold text-base mb-1">Browse All 14+ OEM Brands & Every Product</p>
                <p className="text-[#B2E6D4] text-xs">30 kW · 60 kW · 120 kW · 150 kW · 250 kW+ — specs, connectors, FAME II status and website links</p>
              </div>
              <Link
                to="/charger-catalog"
                className="flex-shrink-0 flex items-center gap-2 bg-[#faf9f6] hover:bg-white text-[#689689] font-semibold text-sm px-5 py-2.5 rounded-xl transition-colors"
              >
                Open Catalog <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            </motion.div>
          </TabsContent>

        </Tabs>
      </main>

      {/* Share Report Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#504136]">
              <Mail className="w-5 h-5 text-[#689689]" />
              Share Report via Email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="share-email">Recipient Email</Label>
              <Input
                id="share-email"
                type="email"
                placeholder="colleague@example.com"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && shareReport()}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="share-message">Personal Message (optional)</Label>
              <Textarea
                id="share-message"
                placeholder="Here's the EV station report for the site we discussed…"
                value={shareMessage}
                onChange={(e) => setShareMessage(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareDialogOpen(false)} disabled={sharing}>
              Cancel
            </Button>
            <Button onClick={shareReport} disabled={sharing} className="bg-[#689689] hover:bg-[#4a7068] text-[#faf9f6]">
              {sharing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />Send Report</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Site & Design sheet */}
      <EditProjectSheet
        open={editSheetOpen}
        onOpenChange={setEditSheetOpen}
        project={project}
        projectId={projectId}
        onSuccess={fetchProject}
      />

      {/* Footer */}
      <footer className="border-t border-[#B2E6D4] bg-white mt-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-12 flex items-center justify-end">
          <Link
            to="/contact"
            className="text-sm text-[#4a7068] hover:text-[#689689] transition-colors flex items-center gap-1.5"
          >
            <MessageSquare className="w-4 h-4" />
            Contact Us
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default ProjectView;
